import React, { useState, useEffect, useCallback, useRef } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import { TileGrid, BackHome, TilesHead } from '../components/Tiles';
import SuggestInput from '../components/SuggestInput';
import SearchSelect from '../components/SearchSelect';
import ShipmentNotes from './ShipmentNotes';
import { productionAPI, warehouseAPI, gradeAPI, salesAPI, branchAPI } from '../api';
import toast from 'react-hot-toast';
import CatalogTree, { useFolders, inFolder } from '../components/CatalogTree';
import { confirmToast } from '../components/ConfirmToast';
import { makeMatcher } from '../utils/search';
import SubstitutesPage from './Substitutes';
import EtalonModes from './EtalonModes';
import AutoPlanPanel from '../components/AutoPlanPanel';

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Сегодняшний день в том виде, в каком его понимает <input type="date"> и
// сервер. Через toISOString() это делать нельзя: он переводит в UTC, и в
// Алматы вечером «сегодня» превращалось бы в завтра.
const todayISO = () => {
  const d = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};

// ─── Услуга: плиту привозит заказчик ──────────────────────────────────────
//
// Технологу это нужно ДО раздачи прессам: под такую позицию не надо ни искать
// плиту на складе, ни держать её в резерве, а оператору придётся взять её из
// привезённой стопки. Со склада такой заказ не закрывается никогда — человек
// ждёт обратно свою плиту, а не такую же с нашего поддона.
const ServiceTag = () => (
  <span title="Плиту привозит заказчик — со склада уходит только плёнка"
    style={{
      background: '#8b5cf618', color: '#8b5cf6', border: '1px solid #8b5cf655',
      borderRadius: 5, padding: '1px 7px', fontSize: 11, fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>🧾 УСЛУГА</span>
);

const PRESS_STATUS_CFG = {
  working:   { label: 'Работает',    color: '#22c55e', icon: '⚙️' },
  idle:      { label: 'Простой',     color: 'var(--dim)', icon: '⏸' },
  retool:    { label: 'Переналадка', color: '#f59e0b', icon: '🔧' },
  breakdown: { label: 'Поломка',     color: '#ef4444', icon: '🔴' },
};

const QUEUE_STATUS_CFG = {
  waiting:     { label: 'Ожидает',    color: 'var(--muted)' },
  in_progress: { label: 'В работе',   color: 'var(--accent)' },
  done:        { label: 'Выполнено',  color: '#22c55e' },
  paused:      { label: 'Пауза',      color: '#f59e0b' },
};

const Btn = ({ children, variant = 'primary', size = 'sm', ...props }) => {
  const bg = {
    primary: 'var(--accent)', success: '#22c55e',
    danger: '#ef4444', ghost: 'transparent', warning: '#f59e0b'
  }[variant];
  const border = variant === 'ghost' ? '1px solid var(--border)' : 'none';
  return (
    <button
      {...props}
      style={{
        background: props.disabled ? 'var(--card)' : bg,
        border, borderRadius: 7,
        // ghost — прозрачная кнопка: белый текст на ней исчезал в светлой теме
        color: props.disabled ? 'var(--mid)' : (variant === 'ghost' ? 'var(--text)' : '#fff'),
        padding: size === 'sm' ? '6px 12px' : '10px 20px',
        fontSize: size === 'sm' ? 13 : 14,
        fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer',
        ...props.style
      }}
    >
      {children}
    </button>
  );
};

// Разделы страницы переехали в боковое меню — гармошка в содержимом
// больше не нужна: где ты и что есть, теперь видно слева.

// ─── Карточка пресса ──────────────────────────────────────────────────────────
function PressCard({ press, textures, onUpdate }) {
  const [changing, setChanging] = useState(false);
  const [newTextureId, setNewTextureId] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const cfg = PRESS_STATUS_CFG[press.status] || PRESS_STATUS_CFG.idle;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (newTextureId) {
        await productionAPI.setPressTexture(press.id, newTextureId);
        toast.success(`${press.name}: текстура изменена`);
      }
      if (newStatus) {
        await productionAPI.setPresStatus(press.id, newStatus);
        toast.success(`${press.name}: статус обновлён`);
      }
      onUpdate();
      setChanging(false);
      setNewTextureId('');
      setNewStatus('');
    } catch {
      toast.error(t("Ошибка обновления"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'var(--bg)', borderRadius: 12, padding: 18,
      border: `1px solid ${cfg.color}30`
    }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16 }}>
            {cfg.icon} {press.name}
          </div>
          <div style={{
            color: cfg.color, fontSize: 12, fontWeight: 600,
            marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.5
          }}>
            {cfg.label}
          </div>
        </div>
        <Btn variant="ghost" onClick={() => setChanging(!changing)}>
          {changing ? '✕' : '⚙ Изменить'}
        </Btn>
      </div>

      {/* Текстура + Норма */}
      <div style={{
        background: 'var(--card)', borderRadius: 8, padding: '8px 12px',
        marginBottom: 12, fontSize: 13,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <span style={{ color: 'var(--dim)' }}>Текстура: </span>
          <span style={{ color: press.current_texture_name ? 'var(--text)' : 'var(--mid)' }}>
            {press.current_texture_name || 'Не задана'}
          </span>
        </div>
        <div>
          <span style={{ color: 'var(--dim)' }}>Норма: </span>
          <span style={{ color: 'var(--text)', fontWeight: 600 }}>
            {press.norm_per_hour} шт/час
          </span>
        </div>
      </div>

      {/* Активное задание */}
      {press.active_job ? (
        <div style={{
          background: 'var(--card)', borderRadius: 8, padding: '10px 12px',
          border: '1px solid #22c55e30'
        }}>
          <div style={{ color: '#22c55e', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            ▶ АКТИВНОЕ ЗАДАНИЕ
          </div>
          {/* Mahsulot nomi + tavsif */}
          {press.active_job.product_name && (
            <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              {press.active_job.product_name}
              {press.active_job.texture_name && (
                <span style={{ color: 'var(--dim)', fontWeight: 400 }}> · {press.active_job.texture_name}</span>
              )}
              {press.active_job.product_description && (
                <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 12 }}> · {press.active_job.product_description}</span>
              )}
            </div>
          )}
          {/* Buyurtma + klient + progress */}
          <div style={{ color: 'var(--dim)', fontSize: 12, marginBottom: 6 }}>
            {press.active_job.client_name && (
              <span>Заказ: <b style={{ color: 'var(--muted)' }}>{press.active_job.client_name}</b>
                {press.active_job.order_number && <span style={{ color: '#f59e0b' }}> · #{press.active_job.order_number}</span>}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
            {press.active_job.quantity_ordered != null && (
              <span style={{ color: 'var(--dim)' }}>
                Нужно: <b style={{ color: 'var(--muted)' }}>{press.active_job.quantity_ordered} шт</b>
              </span>
            )}
            <span style={{ color: 'var(--dim)' }}>
              Сделано: <b style={{ color: '#22c55e' }}>{press.active_job.quantity_done} шт</b>
            </span>
            {press.active_job.quantity_good !== undefined && (
              <span style={{ color: 'var(--dim)' }}>
                Годных: <b style={{ color: '#22c55e' }}>{press.active_job.quantity_good} шт</b>
              </span>
            )}
          </div>
        </div>
      ) : press.waiting_job ? (
        <div style={{ marginTop: 4, background: '#f59e0b12', border: '1px solid #f59e0b40', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{t('⏳ Загружен — ожидает запуска оператором')}</div>
          <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>
            {press.waiting_job.product_name}
            {press.waiting_job.texture_name && (
              <span style={{ color: 'var(--dim)', fontWeight: 400 }}> · {press.waiting_job.texture_name}</span>
            )}
          </div>
          {press.waiting_job.order_number && (
            <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 3 }}>
              Заказ: <b style={{ color: 'var(--muted)' }}>{press.waiting_job.client_name}</b>
              <span style={{ color: '#f59e0b' }}> · #{press.waiting_job.order_number}</span>
            </div>
          )}
          {press.waiting_job.quantity_ordered != null && (
            <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 3 }}>
              Нужно: <b style={{ color: 'var(--muted)' }}>{press.waiting_job.quantity_ordered} шт</b>
            </div>
          )}
        </div>
      ) : (
        <div style={{ color: 'var(--mid)', fontSize: 13, textAlign: 'center', padding: '8px 0' }}>
          Активного задания нет
        </div>
      )}

      {/* Панель изменения */}
      {changing && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--card)' }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 5 }}>
              Сменить текстуру (переналадка)
            </label>
            <select
              value={newTextureId}
              onChange={e => setNewTextureId(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', background: 'var(--card)',
                border: '1px solid var(--border)', borderRadius: 8,
                color: 'var(--text)', fontSize: 13
              }}
            >
              <option value="">{t('— Выбрать текстуру —')}</option>
              {textures.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 5 }}>
              Изменить статус
            </label>
            <select
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', background: 'var(--card)',
                border: '1px solid var(--border)', borderRadius: 8,
                color: 'var(--text)', fontSize: 13
              }}
            >
              <option value="">{t('— Не менять —')}</option>
              <option value="idle">{t('Простой')}</option>
              <option value="retool">{t('Переналадка')}</option>
              <option value="breakdown">{t('Поломка')}</option>
              <option value="working">{t('Работает')}</option>
            </select>
          </div>

          <Btn
            onClick={handleSave}
            disabled={saving || (!newTextureId && !newStatus)}
            style={{ width: '100%' }}
            size="md"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Btn>
        </div>
      )}
    </div>
  );
}

// ─── Модалка назначения пресса ────────────────────────────────────────────────
function AssignPressModal({ item, presses, onClose, onSaved }) {
  const [pressId, setPressId] = useState(item.press || '');
  const [priority, setPriority] = useState(item.priority);
  const [note, setNote] = useState(item.note || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      try {
        await productionAPI.patchQueueItem(item.id, {
          press: pressId || null,
          priority: Number(priority),
          note,
        });
      } catch (err) {
        const d = (err.response && err.response.data) || {};
        // DRF-валидация приходит как {press: ['…']} — показываем сами причины.
        const fields = typeof d === 'object' ? Object.values(d).flat().filter(v => typeof v === 'string').join(' ') : '';
        throw new Error(d.detail || d.error || fields || `Ошибка ${(err.response && err.response.status) || ''}`.trim());
      }
      toast.success(t("Задание обновлено"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000aa',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
    }}>
      <div style={{
        background: 'var(--card)', borderRadius: 16, padding: 28,
        width: '100%', maxWidth: 460
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>
            Назначить пресс
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--dim)', fontSize: 20, cursor: 'pointer'
          }}>✕</button>
        </div>

        {/* Инфо о задании */}
        <div style={{
          background: 'var(--bg)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13
        }}>
          <div style={{ color: 'var(--text)', fontWeight: 600 }}>
            Заказ #{item.order_number}
          </div>
          <div style={{ color: 'var(--muted)', marginTop: 4 }}>
            {item.product_name} — {item.texture_name}
          </div>
          <div style={{ color: 'var(--dim)', marginTop: 2 }}>
            Клиент: {item.client_name} | Кол-во: {item.quantity} шт
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 5 }}>
            Пресс
          </label>
          <select
            value={pressId}
            onChange={e => setPressId(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px', background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14
            }}
          >
            <option value="">{t('— Не назначать —')}</option>
            {presses.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({PRESS_STATUS_CFG[p.status]?.label || p.status})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 5 }}>
            Приоритет (меньше = раньше)
          </label>
          <input
            type="number"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            min="1"
            style={{
              width: '100%', padding: '9px 12px', background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
              fontSize: 14, boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 5 }}>
            Примечание технолога
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder={t("Необязательно...")}
            style={{
              width: '100%', padding: '9px 12px', background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
              fontSize: 14, boxSizing: 'border-box', resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" size="md" onClick={onClose}>{t('Отмена')}</Btn>
          <Btn size="md" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение...' : '✅ Сохранить'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Строка очереди ───────────────────────────────────────────────────────────
function QueueRow({ item, presses, onUpdate }) {
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving]   = useState(false);

  // Тронутую позицию убирать нельзя: за ней стоят листы, движения склада и
  // выгрузка в 1С. Ошибка исправляется, пока по ней ничего не сделано.
  const canRemove = item.status === 'waiting' && !item.quantity_done;

  // Убираем СТРОКУ ЗАКАЗА, а не только очередь: удалить одну очередь мало —
  // позиция вернётся при следующем согласовании, а заказ останется
  // недовыполненным навсегда. Поэтому спрашиваем прямо.
  const remove = async () => {
    const ok = await confirmToast(
      `Убрать «${item.product_name}» из заказа ${item.order_number}?

` +
      'Позиция исчезнет и из очереди, и из заказа. Так убирают то, что ' +
      'записали по ошибке.',
      { confirmText: 'Убрать', danger: true });
    if (!ok) return;
    setRemoving(true);
    try {
      await productionAPI.removeQueueItem(item.id);
      toast.success(t('Позиция убрана'));
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось убрать');
    } finally { setRemoving(false); }
  };
  const cfg = QUEUE_STATUS_CFG[item.status] || QUEUE_STATUS_CFG.waiting;

  const progress = item.quantity > 0
    ? Math.round((item.quantity_done / item.quantity) * 100)
    : 0;

  const isOverdue = item.deadline && new Date(item.deadline) < new Date();

  return (
    <>
      {/* Строка, а не карточка. Карточка занимала сто тридцать точек: номер,
          продукт, полоса прогресса, пресс, срок и кнопка — всё столбиком. При
          двадцати шести позициях это два с половиной экрана прокрутки, и
          сравнить строки между собой глазом уже нельзя. Здесь те же данные в
          одну линию: на экран помещается вся очередь целиком. */}
      <div style={{
        background: 'var(--bg)', borderRadius: 9,
        padding: '8px 12px',
        borderLeft: `3px solid ${cfg.color}`,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span style={{
          background: 'var(--card)', color: 'var(--muted)', borderRadius: 6,
          padding: '2px 8px', fontSize: 12, fontWeight: 700, flexShrink: 0,
        }}>#{item.priority}</span>

        <span style={{ flex: '1 1 190px', minWidth: 0, fontSize: 13.5 }}>
          <b style={{ color: 'var(--text)' }}>{item.order_number}</b>
          <span style={{ color: 'var(--dim)' }}> · {item.client_name}</span>
          {isOverdue && (
            <span style={{
              background: '#ef444418', color: '#ef4444', borderRadius: 5,
              padding: '1px 6px', fontSize: 11, fontWeight: 600, marginLeft: 6,
            }}>просрочен</span>
          )}
          {/* Выписано на будущий день: у пресса этой строки ещё нет и не
              будет до самого дня. Без метки технолог видел бы её в очереди
              и не понимал, почему оператор за неё не берётся. */}
          {item.is_service && <> <ServiceTag /></>}
          {item.plan_date && item.plan_date > todayISO() && (
            <span title={t('Пресс увидит её только в этот день')} style={{
              background: '#0ea5e918', color: '#0284c7', borderRadius: 5,
              padding: '1px 6px', fontSize: 11, fontWeight: 700, marginLeft: 6,
            }}>📅 {new Date(item.plan_date).toLocaleDateString('ru-RU')}</span>
          )}
        </span>

        <span style={{
          flex: '2 1 240px', minWidth: 0, color: 'var(--muted)', fontSize: 13,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.product_name}
          <span style={{ color: 'var(--mid)' }}> · {item.texture_name}</span>
        </span>

        {/* Сделано из плана — цифрой и тонкой чертой под ней: полоса во всю
            ширину строки съедала место, а читают всё равно цифру */}
        <span style={{ width: 78, flexShrink: 0, textAlign: 'right' }}>
          <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700 }}>
            {item.quantity_done}/{item.quantity}
          </span>
          <span style={{ display: 'block', height: 3, borderRadius: 3,
                         background: 'var(--card)', marginTop: 3 }}>
            <span style={{
              display: 'block', height: 3, borderRadius: 3, width: `${progress}%`,
              background: progress === 100 ? '#22c55e' : 'var(--accent)',
            }} />
          </span>
        </span>

        <span style={{ width: 116, flexShrink: 0, fontSize: 12,
                       color: item.press_name ? 'var(--dim)' : '#ef4444',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.press_name ? '⚙ ' + item.press_name : 'пресс не назначен'}
        </span>

        <span style={{ width: 78, flexShrink: 0, fontSize: 12,
                       color: isOverdue ? '#ef4444' : 'var(--dim)' }}>
          {item.deadline || ''}
        </span>

        <span style={{
          flexShrink: 0, background: `${cfg.color}18`, color: cfg.color,
          borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600,
        }}>{cfg.label}</span>

        {item.status === 'waiting' && (
          <Btn size="sm" onClick={() => setAssigning(true)}>
            {item.press ? '✏' : '+ пресс'}
          </Btn>
        )}
        {/* Записали не тот товар — позицию надо убрать, а не обходить.
            Иначе цех спрашивает про неё каждый день, а заказ, в котором она
            лежит, никогда не закроется. */}
        {canRemove && (
          <button onClick={remove} disabled={removing} title={t('Убрать позицию')}
            style={{
              background: 'none', border: '1px solid var(--border)',
              borderRadius: 7, color: '#ef4444', padding: '4px 9px',
              fontSize: 13, cursor: removing ? 'wait' : 'pointer',
              fontFamily: 'inherit', flexShrink: 0,
            }}>✕</button>
        )}
        {/* Примечание технолога — во всю ширину под строкой: оно бывает
            длинным, и в общий ряд его не втиснуть */}
        {item.note && (
          <div style={{
            flexBasis: '100%', marginTop: 6, paddingTop: 6,
            borderTop: '1px solid var(--card)', color: 'var(--dim)', fontSize: 12.5,
          }}>
            💬 {item.note}
          </div>
        )}
      </div>

      {assigning && (
        <AssignPressModal
          item={item}
          presses={presses}
          onClose={() => setAssigning(false)}
          onSaved={onUpdate}
        />
      )}
    </>
  );
}

// ─── Пресс параметрлари модали ──────────────────────────────────────────────────
function ParamsModal({ nom, matrices, onClose, onSaved }) {
  const [matrix, setMatrix] = useState(nom.matrix ? String(nom.matrix) : '');
  const [temp, setTemp]     = useState(nom.press_temperature ?? '');
  const [hold, setHold]     = useState(nom.press_hold_seconds ?? '');
  const [press, setPress]   = useState(nom.press_pressure ?? '');
  const [thick, setThick]   = useState(nom.thickness ?? '');
  // Порядок в смене собирают от тёмного к светлому: плита приходит с пылью,
  // на прессе копится грязь, и на тёмном ЛДСП её не видно — а к светлым
  // пресс успевают протереть. Наоборот не работает: сор после светлой партии
  // всплывёт на первом же тёмном листе.
  const [tone, setTone]     = useState(
    nom.color_tone === null || nom.color_tone === undefined ? '' : String(nom.color_tone));
  const [saving, setSaving] = useState(false);

  // Матрица танланганда/ўзгартирилганда — параметрларни ўша матрицадан тортиш.
  // Аввалги гуард (!temp && ...) фақат биринчи мартагина тўлдирарди; матрица номи
  // алмаштирилса пастдаги қаторлар ўзгармай қоларди. Энди ҳар алмаштиришда қайта тортилади.
  const onMatrixChange = (val) => {
    setMatrix(val);
    const t = matrices.find(x => String(x.id) === String(val));
    if (t) {
      setTemp(t.press_temperature ?? '');
      setHold(t.press_hold_seconds ?? '');
      setPress(t.press_pressure ?? '');
    }
  };

  const INP = { width: '100%', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' };
  const LBL = { display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 };

  const save = async () => {
    setSaving(true);
    try {
      await warehouseAPI.updateNomenclature(nom.id, {
        matrix:             matrix ? Number(matrix) : null,
        // Текстовые параметры (CharField): «/», диапазоны и точки допустимы.
        press_temperature:  String(temp  ?? '').trim(),
        press_hold_seconds: String(hold  ?? '').trim(),
        press_pressure:     String(press ?? '').trim(),
        // Толщина — число (или пусто, если не указана)
        thickness:          String(thick ?? '').trim() === ''
                              ? null : Number(thick),
        // Пусто — тон угадается по названию, и в заявке это помечено
        color_tone:         String(tone ?? '').trim() === ''
                              ? null : Math.max(0, Math.min(100, Number(tone))),
      });
      toast.success(t("✅ Параметры сохранены"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000cc',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20,
    }}>
      <div style={{ background: 'var(--card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>{t('⚙️ Параметры прессования')}</h3>
            <p style={{ color: 'var(--dim)', fontSize: 12, marginTop: 4 }}>{nom.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={LBL}>{t('Матрица (текстура)')}</label>
            <select style={INP} value={matrix} onChange={e => onMatrixChange(e.target.value)}>
              <option value="">{t('— не указана —')}</option>
              {matrices.map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              {/* «190/195» — это НЕ коридор, а две плиты: низ 190, верх 195.
                  Так это читают в цехе, и так же сравнивает отчёт. Пока в
                  подписи стояло просто «°C», порядок держали в голове, и
                  часть матриц заведена наоборот. */}
              <label style={LBL}>🌡 °C — низ / верх</label>
              <input type="text" style={INP} value={temp} onChange={e => setTemp(e.target.value)} placeholder="190 / 195" />
            </div>
            <div>
              <label style={LBL}>{t('⏱ сек')}</label>
              <input type="text" style={INP} value={hold} onChange={e => setHold(e.target.value)} placeholder="300" />
            </div>
            <div>
              <label style={LBL}>{t('💪 бар')}</label>
              <input type="text" style={INP} value={press} onChange={e => setPress(e.target.value)} placeholder="25" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10 }}>
            <div>
              <label style={LBL}>{t('📏 Толщина (мм)')}</label>
              <input type="number" step="0.01" min="0" style={INP} value={thick}
                onChange={e => setThick(e.target.value)} placeholder="16" />
            </div>
            <div>
              <label style={LBL}>{t('🎨 Тон цвета (0 — чёрный, 100 — белый)')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="number" min="0" max="100" style={{ ...INP, width: 100 }}
                  value={tone} onChange={e => setTone(e.target.value)}
                  placeholder={t('не задан')} />
                {String(tone).trim() !== '' && (
                  <span style={{
                    width: 30, height: 30, borderRadius: 6, flexShrink: 0,
                    border: '1px solid var(--border)',
                    background: `rgb(${Math.round(255 * Math.max(0, Math.min(100, Number(tone))) / 100)},${Math.round(255 * Math.max(0, Math.min(100, Number(tone))) / 100)},${Math.round(255 * Math.max(0, Math.min(100, Number(tone))) / 100)})`,
                  }} />
                )}
              </div>
            </div>
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 11.5, lineHeight: 1.5 }}>
            По тону собирается порядок смены: тёмные идут первыми, пока пресс
            не протёрли, светлые — после. Пусто — тон угадается по названию, и
            в заявке такая строка помечена пунктиром.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <Btn variant="success" size="md" disabled={saving} onClick={save} style={{ flex: 1 }}>
            {saving ? 'Сохранение...' : '✅ Сохранить'}
          </Btn>
          <Btn variant="ghost" size="md" onClick={onClose}>{t('Отмена')}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Рецепты пачкой ──────────────────────────────────────────────────────────
//
// Рецепт — единственное место, откуда производство узнаёт, что списать со
// склада. Пока его нет, смена работает, выпуск считается, а ДСП и плёнка
// остаются на остатке навсегда: он растёт от приходов из 1С и никогда не
// падает от того, что реально спрессовали.
//
// По одному их никто не заполнит — их десятки. Но по одному и не надо:
//   • плита зависит ТОЛЬКО от толщины и основы, декор тут ни при чём;
//   • плёнка зависит ТОЛЬКО от декора, толщина ни при чём.
// Отсюда два блока вместо длинного списка позиций: плиту выбирают раз на
// толщину, плёнку — раз на декор.
//
// Плёнку система предлагает сама: и ЛДСП, и плёнка начинаются с одного и того
// же названия декора («Бетон Пайн светлый …» ↔ «Бетон Пайн Светлый Пленка
// меламиновая U31148 …»). Это подсказка, а не истина — плёнок одного декора
// бывает несколько, и какая идёт в дело, знает только технолог.
// ─── Пересчёт смен, закрытых без рецепта ─────────────────────────────────────
//
// Заявку в производство не держим из-за пустого рецепта: пресс должен
// работать, а не ждать технолога. Но такая смена закрывается, ничего не
// списав, и остаток ДСП с плёнкой остаётся выше настоящего.
//
// Рецепт появился позже — значит, теперь известно, что эти смены съели.
// Показываем это ДО записи: пересчёт может увести остаток глубоко в минус, и
// увидеть цифру человек должен заранее, а не после.
function BackfillPanel() {
  const [info, setInfo]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [open, setOpen]     = useState(false);

  const load = useCallback(() => {
    salesAPI.previewBackfill()
      .then(r => setInfo(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);


  if (!info || !info.sessions) return null;

  const run = async () => {
    const ok = await confirmToast(
      `Списать сырьё по ${info.sessions} сменам, закрытым без рецепта? `
      + 'Остаток ДСП и плёнки уменьшится.',
      { confirmText: 'Пересчитать' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await salesAPI.runBackfill();
      toast.success(`✅ ${t('Пересчитано смен')}: ${data.sessions}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      background: 'var(--bg)', border: '1px solid var(--warning)',
      borderRadius: 10, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>
            ⏳ {info.sessions} смен закрылись без рецепта
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            Рецепт у этих позиций уже есть, а сырьё по ним так и не списано —
            остаток ДСП и плёнки выше настоящего. Смены, закрытые с рецептом,
            пересчёт не тронет.
          </div>
        </div>
        <button onClick={() => setOpen(!open)} style={{
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8,
          color: 'var(--muted)', padding: '9px 14px', fontSize: 13,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>{open ? 'Скрыть' : 'Что спишется'}</button>
        <button onClick={run} disabled={busy} style={{
          background: busy ? 'var(--card)' : 'var(--warning)', border: 'none',
          borderRadius: 8, color: busy ? 'var(--dim)' : '#fff', padding: '9px 18px',
          fontSize: 13.5, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
        }}>{busy ? 'Считаем…' : 'Пересчитать прошлые смены'}</button>
      </div>

      {open && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr>
                {['Сырьё', 'Спишется', 'Остаток сейчас', 'Станет'].map((h, i) => (
                  <th key={h} style={{
                    textAlign: i ? 'right' : 'left', padding: '6px 10px', fontSize: 11,
                    color: 'var(--dim)', textTransform: 'uppercase',
                    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {info.materials.map(m => (
                <tr key={m.id}>
                  <td style={{ padding: '6px 10px', fontSize: 12.5, color: 'var(--text)',
                               borderBottom: '1px solid var(--border)' }}>{m.name}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12.5, textAlign: 'right',
                               color: 'var(--text)', borderBottom: '1px solid var(--border)',
                               whiteSpace: 'nowrap' }}>−{m.qty}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12.5, textAlign: 'right',
                               color: 'var(--muted)', borderBottom: '1px solid var(--border)',
                               whiteSpace: 'nowrap' }}>{m.stock_now}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12.5, textAlign: 'right',
                               fontWeight: 700, whiteSpace: 'nowrap',
                               color: m.stock_after < 0 ? 'var(--danger)' : 'var(--text)',
                               borderBottom: '1px solid var(--border)' }}>{m.stock_after}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {info.materials.some(m => m.stock_after < 0) && (
            <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              Красное — остаток уйдёт в минус. Это не ошибка пересчёта: значит,
              приходы этого сырья в MES меньше, чем реально израсходовано.
              Сверять придётся с 1С.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Рецепт: сколько сырья уходит на один лист ───────────────────────────────
//
// Обычный лист — это плита и плёнка, поэтому две строки предлагаются сразу и
// подписаны. Но так бывает не всегда: на образец кладут разную плёнку с двух
// сторон, а со временем в рецепт войдёт и клей. Поэтому строк может быть
// сколько нужно, и материал в каждой выбирается из всего справочника.
//
// Рядом с выбранным материалом показан его вид — плита, плёнка или прочее.
// Две плёнки в рецепте должны читаться как две плёнки, а не как «две строки».
function RecipeModal({ item, recipe, films, dsps, materials, onClose, onSaved }) {
  // Весь справочник сырья одним списком: строка не привязана к виду заранее,
  // иначе вторую плёнку было бы некуда положить.
  const all = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of [...(dsps || []), ...(films || []), ...(materials || [])]) {
      if (!m || seen.has(String(m.id))) continue;
      seen.add(String(m.id));
      out.push(m);
    }
    return out;
  }, [dsps, films, materials]);

  // Плиту и плёнку кладут листами: половины листа не бывает, а стрелка с шагом
  // 0.01 давала «1.01 плёнки» — и это молча уезжало в списание. Клей и прочее
  // считают в килограммах, там дробь нужна.
  const isWhole = (id) => {
    const m = all.find(x => String(x.id) === String(id));
    if (!m) return true;
    return m.category === 'dsp' || m.category === 'paper'
      || (dsps || []).some(d => String(d.id) === String(id))
      || (films || []).some(f => String(f.id) === String(id));
  };

  const kindOf = (id) => {
    const m = all.find(x => String(x.id) === String(id));
    if (!m) return null;
    if ((dsps || []).some(d => String(d.id) === String(id)))  return { icon: '🪵', label: 'плита' };
    if ((films || []).some(f => String(f.id) === String(id))) return { icon: '📄', label: 'плёнка' };
    return { icon: '📦', label: m.category === 'dsp' ? 'плита'
                          : m.category === 'paper' ? 'плёнка' : 'прочее' };
  };

  const prod = recipe
    ? { id: recipe.product, name: recipe.product_name, film_options: [] }
    : item;

  // Существующий рецепт открываем как есть — сколько строк было, столько и
  // покажем. Раньше всё, что не легло в плиту и плёнку, только перечислялось
  // текстом и правке не поддавалось.
  const initial = () => {
    if (recipe && (recipe.items || []).length) {
      return recipe.items.map(i => ({
        material: String(i.material), quantity: String(Number(i.quantity)),
      }));
    }
    return [
      { material: prod.dsp_suggested ? String(prod.dsp_suggested) : '', quantity: '1' },
      { material: prod.film_suggested ? String(prod.film_suggested) : '', quantity: '2' },
    ];
  };

  const [rows, setRows]     = useState(initial);
  const [saving, setSaving] = useState(false);
  // Какой позиции какая группа замены соответствует. Технолог должен видеть,
  // что смена возьмёт не обязательно ЭТУ плиту: у неё есть равноценные, и
  // если её не станет, спишется соседняя. Иначе строка расхода в 1С
  // выглядела бы подменой.
  const [gmap, setGmap] = useState({});
  useEffect(() => {
    warehouseAPI.getGroups()
      .then(({ data }) => {
        const m = {};
        (data.results || data || []).forEach(g => {
          if (g.is_reject) return;
          (g.items || []).forEach(i => { m[String(i.nomenclature)] = g; });
        });
        setGmap(m);
      })
      .catch(() => {});
  }, []);
  // Спрашиваем прямо в панели: всплывашка выходила в угол экрана, мимо глаз, и
  // технолог жал «Сохранить» ещё дважды, пока в углу копились три вопроса.
  const [confirm, setConfirm] = useState(null);   // { list, items }

  const setRow = (i, field, val) =>
    setRows(rs => rs.map((r, k) => (k === i ? { ...r, [field]: val } : r)));
  const addRow = () => setRows(rs => [...rs, { material: '', quantity: '1' }]);
  const dropRow = (i) => setRows(rs => rs.filter((_, k) => k !== i));

  const filled = rows.filter(r => r.material);

  const save = async () => {
    if (!filled.length) { toast.error(t('Добавьте хотя бы один материал')); return; }
    const dup = filled.map(r => String(r.material));
    if (new Set(dup).size !== dup.length) {
      toast.error(t('Один материал добавлен дважды — сложите их в одну строку'));
      return;
    }
    const nameOf = (id) => (all.find(x => String(x.id) === String(id)) || {}).name || '';
    setConfirm({
      rows: filled.map(r => ({
        name: nameOf(r.material), qty: Number(r.quantity) || 0,
        kind: kindOf(r.material),
        group: (gmap[String(r.material)] || {}).name || '',
      })),
      items: filled.map(r => ({
        material: Number(r.material), quantity: Number(r.quantity) || 1,
      })),
    });
  };

  const commit = async (items) => {
    setConfirm(null);
    setSaving(true);
    try {
      if (recipe) {
        const { data } = await salesAPI.updateRecipe(recipe.id, {
          product: recipe.product, is_active: true, items,
        });
        // Рецепт поправили — сырьё под уже взятые заказы считается по-новому.
        // Молчать об этом нельзя: у менеджера остаток изменился прямо сейчас.
        const r = data?.reservations;
        toast.success(`✅ ${t('Рецепт обновлён')}`
          + (r?.orders ? ` · ${t('пересчитаны брони заказов')}: ${r.orders}` : ''));
        if (r?.minus?.length) {
          toast.error(`⚠ ${t('Сырья не хватает')}: ${r.minus.slice(0, 3).join(', ')}`,
                      { autoClose: 12000 });
        }
      } else {
        const { data } = await salesAPI.applyRecipes({ groups: [{
          product_ids: [prod.id],
          items: items.map(x => ({ material_id: x.material, quantity: x.quantity })),
        }] });
        toast.success(`✅ ${t('Рецепт создан')}`
          + (data.backfilled ? ` · ${t('пересчитано смен')}: ${data.backfilled}` : ''));
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data || {};
      const first = Array.isArray(d.product) ? d.product[0] : null;
      toast.error(d.error || first || d.detail || 'Ошибка');
    } finally { setSaving(false); }
  };

  const QTY = {
    background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', padding: '10px 12px', fontSize: 16, fontFamily: 'inherit',
    width: 78, textAlign: 'center', fontWeight: 800,
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--card)', borderLeft: '1px solid var(--border)',
        width: 620, maxWidth: '96vw', height: '100vh', padding: '22px 24px',
        overflowY: 'auto', boxShadow: '-12px 0 32px -20px rgba(0,0,0,.6)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ color: 'var(--text)', fontWeight: 800, fontSize: 17, marginBottom: 4 }}>
          {recipe ? t('Рецепт') : t('Новый рецепт')}
        </div>
        {/* Название целиком, в несколько строк: по хвосту («80 г/кв.м
            2760*1830») и различают, какая это бумага. */}
        <div style={{
          color: 'var(--text)', fontSize: 14, fontWeight: 600, lineHeight: 1.4,
          marginBottom: 3, wordBreak: 'break-word',
        }}>
          {prod.name}
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 18 }}>
          {t('расход на 1 лист')}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {rows.map((r, i) => {
            const k = kindOf(r.material);
            return (
              <div key={i} style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                padding: '9px 11px', borderRadius: 9,
                background: 'var(--bg)', border: '1px solid var(--border)',
              }}>
                {/* Вид — отдельной строкой сверху: рядом с полем он съедал
                    ширину у названия, а именно название тут и читают */}
                <div style={{
                  fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em',
                  color: k ? 'var(--muted)' : 'var(--dim)',
                }}>
                  {k ? `${k.icon} ${k.label.toUpperCase()}` : `ПОЗИЦИЯ ${i + 1}`}
                  {gmap[String(r.material)] && (
                    <span style={{ color: '#22c55e', fontWeight: 600, marginLeft: 8 }}>
                      ↺ {gmap[String(r.material)].name}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <SearchSelect
                  options={all}
                  suggested={i === 1 ? (prod.film_options || []) : []}
                  suggestedLabel={prod.exact ? 'Совпало по названию' : 'Похожие по названию'}
                  value={r.material}
                  onChange={(v) => setRow(i, 'material', v)}
                  placeholder={t('— выбрать материал —')} />
                <input type="number" min={0}
                       step={isWhole(r.material) ? 1 : 0.01}
                       style={QTY}
                       value={r.quantity}
                       onChange={e => setRow(i, 'quantity', isWhole(r.material)
                         ? String(Math.max(0, Math.round(Number(e.target.value) || 0)))
                         : e.target.value)} />
                <button onClick={() => dropRow(i)}
                  disabled={rows.length <= 1}
                  title={t('Убрать строку')}
                  style={{
                    background: 'none', border: 'none', fontSize: 18, padding: '0 4px',
                    color: rows.length <= 1 ? 'var(--dim)' : '#ef4444',
                    cursor: rows.length <= 1 ? 'default' : 'pointer',
                  }}>✕</button>
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={addRow} style={{
          marginTop: 11, background: 'transparent', border: '1px dashed var(--border)',
          borderRadius: 8, color: 'var(--muted)', padding: '9px 16px', fontSize: 13,
          cursor: 'pointer', width: '100%', fontFamily: 'inherit',
        }}>
          + {t('Добавить позицию')}
        </button>

        <div style={{ color: 'var(--dim)', fontSize: 12, lineHeight: 1.5, marginTop: 11 }}>
          {t('Обычный лист — плита и плёнка. Позиций может быть больше: на образец '
             + 'кладут разную плёнку с двух сторон, а расход клея добавляют отдельной строкой.')}
        </div>

        {confirm && (
          <div style={{
            marginTop: 16, background: 'var(--bg)', borderRadius: 10,
            border: '1px solid var(--accent)', padding: '14px 16px',
          }}>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              {t('Проверьте: на 1 лист уйдёт')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
              {confirm.rows.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 12,
                  fontSize: 13.5, color: 'var(--text)',
                }}>
                  <span style={{ minWidth: 0, wordBreak: 'break-word' }}>
                    {r.kind ? r.kind.icon + ' ' : ''}{r.name}
                    {r.group && (
                      <span style={{ display: 'block', color: '#22c55e', fontSize: 11.5 }}>
                        ↺ {t('заменяется в группе')} «{r.group}»
                      </span>
                    )}
                  </span>
                  <b style={{ whiteSpace: 'nowrap' }}>× {r.qty}</b>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 9 }}>
              <button onClick={() => commit(confirm.items)} disabled={saving} style={{
                flex: 2, background: 'var(--accent)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '11px 0', fontSize: 14.5, fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>
                {saving ? '…' : (recipe ? t('Да, сохранить') : t('Да, создать'))}
              </button>
              <button onClick={() => setConfirm(null)} disabled={saving} style={{
                flex: 1, background: 'var(--card)', color: 'var(--muted)',
                border: '1px solid var(--border)', borderRadius: 8, padding: '11px 0',
                fontSize: 14.5, cursor: 'pointer', fontFamily: 'inherit',
              }}>{t('Изменить')}</button>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex', gap: 10, marginTop: 'auto', paddingTop: 18,
          opacity: confirm ? .4 : 1, pointerEvents: confirm ? 'none' : 'auto',
        }}>
          <button onClick={save} disabled={saving} style={{
            flex: 2, background: 'var(--accent)', color: '#fff', border: 'none',
            borderRadius: 9, padding: '12px 0', fontSize: 15, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>
            {saving ? '…' : (recipe ? t('Сохранить') : t('Создать рецепт'))}
          </button>
          <button onClick={onClose} style={{
            flex: 1, background: 'var(--bg)', color: 'var(--muted)',
            border: '1px solid var(--border)', borderRadius: 9, padding: '12px 0',
            fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
          }}>{t('Отмена')}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Рецепты ─────────────────────────────────────────────────────────────────
//
// Рецепт решает, сколько ДСП и плёнки списать за смену: без него смена идёт,
// выпуск считается, а сырьё остаётся на остатке навсегда. Поэтому главный
// вопрос страницы — не «какие рецепты есть», а «по каким позициям их ещё нет»,
// и он вынесен в фильтр с числом прямо на кнопке.
//
// Своя страница, не вкладка справочника: рецепт — это не карточка товара, к
// нему возвращаются отдельно и подолгу.
export function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [sug, setSug]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode]       = useState('none');   // 'none' | 'have'
  const [q, setQ]             = useState('');
  const [open, setOpen]       = useState(null);
  const [scope, setScope]     = useState('work');   // 'work' | 'all'

  // По умолчанию показываем то, что уже в работе: там счёт идёт на часы. Но
  // рецепты заполняют и заранее, а такую позицию узкий список не показывал — и
  // завести ей рецепт было негде. «Весь ЛДСП» открывает справочник целиком.
  const load = useCallback(() => {
    setLoading(true);
    const params = scope === 'all' ? { scope: 'all' } : undefined;
    Promise.all([salesAPI.getRecipes(), salesAPI.suggestRecipes(params)])
      .then(([r, s]) => { setRecipes(r.data || []); setSug(s.data); })
      .catch(() => toast.error(t('Не удалось загрузить рецепты')))
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // Снять рецепт. Сырьё, уже списанное по нему, не трогаем: смены закрыты, и
  // переигрывать их задним числом — значит второй раз поменять остаток.
  const removeRecipe = async (r) => {
    const list = (r.items || [])
      .map(i => `${i.material_name || i.material} × ${Number(i.quantity)}`).join(', ');
    const ok = await confirmToast(
      `Снять рецепт с «${r.product_name}»?\n\nСейчас: ${list || 'пусто'}.\n`
      + 'Товар вернётся в список «Без рецепта» — там его заведут заново. '
      + 'Уже списанное сырьё не изменится.',
      { danger: true, confirmText: 'Снять рецепт' });
    if (!ok) return;
    try {
      await salesAPI.deleteRecipe(r.id);
      toast.success(t('Рецепт снят'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Ошибка');
    }
  };

  if (loading) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>;

  // Позиции без рецепта — с подсказками своей группы: плита от толщины,
  // плёнка от декора
  const missing = [];
  ((sug && sug.groups) || []).forEach(g => {
    const tk = ((sug && sug.thicknesses) || []).find(
      x => x.thickness === g.thickness && x.base === g.base) || {};
    g.products.forEach(p => missing.push({
      ...p, decor: g.decor, base: g.base, thickness: g.thickness,
      exact: g.exact, film_options: g.film_options,
      film_suggested: g.film_suggested, dsp_suggested: tk.dsp_suggested || null,
    }));
  });

  const needle = q.trim().toLowerCase();
  const hit = (n) => makeMatcher(needle)(n);
  const shownMissing = missing.filter(m => hit(m.name))
    .sort((a, b) => (a.exact === b.exact ? a.name.localeCompare(b.name) : (a.exact ? -1 : 1)));
  const shownHave = recipes.filter(r => hit(r.product_name))
    .sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));

  const chip = (on) => ({
    background: on ? 'var(--accent)' : 'var(--bg)',
    border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
    borderRadius: 8, color: on ? '#fff' : 'var(--muted)',
    padding: '8px 16px', fontSize: 13.5, cursor: 'pointer', fontWeight: 600,
    fontFamily: 'inherit',
  });
  const ROW = {
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
    flexWrap: 'wrap',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 800 }}>
        Рецепт говорит производству, сколько ДСП и плёнки списать за смену. Пока
        его нет, смена работает и выпуск считается, но сырьё со склада не
        уходит — остаток остаётся выше настоящего.
      </div>

      <BackfillPanel />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setMode('none')} style={chip(mode === 'none')}>
          Без рецепта · {missing.length}
        </button>
        <button onClick={() => setMode('have')} style={chip(mode === 'have')}>
          С рецептом · {recipes.length}
        </button>
        {mode === 'none' && (
          <button
            onClick={() => setScope(scope === 'all' ? 'work' : 'all')}
            title={t('Показать весь справочник ЛДСП, а не только то, что в работе')}
            style={{
              background: scope === 'all' ? 'var(--accent-weak)' : 'var(--bg)',
              border: '1px solid ' + (scope === 'all' ? 'var(--accent)' : 'var(--border)'),
              borderRadius: 8, color: scope === 'all' ? 'var(--accent)' : 'var(--muted)',
              padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
              fontFamily: 'inherit',
            }}>
            {scope === 'all' ? '✓ ' : ''}{t('Весь ЛДСП')}
          </button>
        )}
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Поиск по названию…')}
          style={{
            flex: '1 1 220px', minWidth: 180, background: 'var(--bg)',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
            padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
          }} />
      </div>

      {mode === 'none' && (
        shownMissing.length === 0 ? (
          <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '36px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>✅</div>
            <div style={{ color: 'var(--text)', fontWeight: 700 }}>
              {needle ? t('Ничего не найдено') : t('Все рецепты на месте')}
            </div>
            {!needle && (
              <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6 }}>
                {scope === 'all'
                  ? t('Во всём справочнике ЛДСП рецепт есть у каждой позиции.')
                  : t('Позиций без рецепта, которые делают или вот-вот начнут, нет. '
                      + 'Нажмите «Весь ЛДСП», чтобы заполнить рецепты заранее.')}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shownMissing.map(it => (
              <div key={it.id} style={{ ...ROW, borderLeft: `3px solid ${it.exact ? '#22c55e' : 'var(--border)'}` }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{it.name}</div>
                  <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 3 }}>
                    {it.base || '—'}{it.thickness ? ' · ' + it.thickness + ' мм' : ''}{' · '}
                    {it.exact
                      ? <span style={{ color: '#22c55e' }}>плёнка найдена по названию</span>
                      : <span>плёнку нужно выбрать</span>}
                  </div>
                </div>
                <button onClick={() => setOpen({ item: it })} style={{
                  background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff',
                  padding: '9px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', flexShrink: 0,
                }}>Создать рецепт →</button>
              </div>
            ))}
          </div>
        )
      )}

      {mode === 'have' && (
        shownHave.length === 0 ? (
          <div style={{ color: 'var(--mid)', fontSize: 13, padding: 20, textAlign: 'center' }}>
            {t('Ничего не найдено')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shownHave.map(r => (
              <div key={r.id} style={{ ...ROW, borderLeft: '3px solid #22c55e' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{r.product_name}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 3 }}>
                    {(r.items || []).map(i => `${i.material_name || i.material} × ${Number(i.quantity)}`).join('  ·  ')
                      || 'пусто'}
                  </div>
                </div>
                <button onClick={() => setOpen({ recipe: r })} style={{
                  background: 'var(--accent-weak)', border: '1px solid var(--accent-weak)',
                  borderRadius: 8, color: 'var(--accent)', padding: '9px 18px', fontSize: 13.5,
                  fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                }}>✏ Изменить</button>
                {/* Снять рецепт можно было только из карточки товара в
                    справочнике — а живут рецепты здесь, и искать их там никто
                    не догадывался. Рецепт заводят заново тут же, фильтром
                    «Без рецепта». */}
                <button onClick={() => removeRecipe(r)} style={{
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--muted)', padding: '9px 14px',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', flexShrink: 0,
                }}>🧾 Снять</button>
              </div>
            ))}
          </div>
        )
      )}

      {open && (
        <RecipeModal
          item={open.item} recipe={open.recipe}
          films={(sug && sug.films) || []} dsps={(sug && sug.dsps) || []}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Короткие имена ──────────────────────────────────────────────────────────
//
// Полное имя приходит из 1С и на экранах цеха читается плохо:
//   «Образец Дуб Бунратти/Дуб Маувелла (мягкое дерево) ЛДСП, 2750*1830*16 мм.»
// Оператор у пресса и кладовщик ищут глазами декор, а читают служебную строку —
// и в спешке промахиваются. В документах и обмене с 1С полное имя остаётся:
// там оно и есть настоящее.
//
// Подсказку собирает система (декор + размер), но последнее слово за
// технологом: имя товара знает он, а не разбор строки.
function ShortNames() {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [onlyEmpty, setOE]  = useState(true);
  const [q, setQ]           = useState('');
  const [edit, setEdit]     = useState({});     // id -> текст в поле
  const [busy, setBusy]     = useState(false);

  const load = useCallback(() => {
    setLoad(true);
    warehouseAPI.getShortNames({ only_empty: onlyEmpty ? 1 : 0, q: q.trim() || undefined })
      .then(r => { setData(r.data); setEdit({}); })
      .catch(() => toast.error(t('Не удалось загрузить список')))
      .finally(() => setLoad(false));
  }, [onlyEmpty, q]);

  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);

  const generate = async () => {
    const ok = await confirmToast(
      `Проставить подсказки всем позициям без короткого имени (${data.empty})? `
      + 'Каждую можно поправить руками после.',
      { confirmText: 'Проставить' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: res } = await warehouseAPI.saveShortName({ generate: true });
      toast.success(`✅ ${t('Заполнено')}: ${res.filled}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  const save = async (row, value) => {
    try {
      await warehouseAPI.saveShortName({ id: row.id, short_name: value });
      setData(d => ({ ...d, results: d.results.map(r => r.id === row.id ? { ...r, short_name: value } : r) }));
      setEdit(e => { const n = { ...e }; delete n[row.id]; return n; });
      toast.success(t('Сохранено'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    }
  };

  if (loading && !data) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>;
  if (!data) return null;

  const INP = {
    width: '100%', background: 'var(--input)', border: '1px solid var(--border)',
    borderRadius: 8, color: 'var(--text)', padding: '8px 10px', fontSize: 13,
    fontFamily: 'inherit', boxSizing: 'border-box',
  };
  const chip = (on) => ({
    background: on ? 'var(--accent)' : 'var(--bg)',
    border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
    borderRadius: 8, color: on ? '#fff' : 'var(--muted)', padding: '8px 16px',
    fontSize: 13, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 800 }}>
        Полное название приходит из 1С и на экранах цеха читается плохо. Короткое
        имя показывается оператору у пресса и кладовщику; в документах и обмене
        с 1С остаётся полное. Подсказку собирает система — декор и размер, —
        но поправить её может только человек.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setOE(true)} style={chip(onlyEmpty)}>
          Без имени · {data.empty}
        </button>
        <button onClick={() => setOE(false)} style={chip(!onlyEmpty)}>
          Все · {data.empty + data.named}
        </button>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Поиск по полному названию…')}
          style={{ ...INP, flex: '1 1 220px', minWidth: 180, width: 'auto' }} />
        {data.empty > 0 && (
          <button onClick={generate} disabled={busy} style={{
            background: busy ? 'var(--bg)' : 'var(--accent)', border: 'none',
            borderRadius: 8, color: busy ? 'var(--dim)' : '#fff', padding: '8px 18px',
            fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}>{busy ? 'Считаем…' : `✨ Заполнить подсказками (${data.empty})`}</button>
        )}
      </div>

      {data.results.length === 0 ? (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '36px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>✅</div>
          <div style={{ color: 'var(--text)', fontWeight: 700 }}>
            {q ? t('Ничего не найдено') : t('Все позиции названы')}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.results.map(r => {
            const val = edit[r.id] !== undefined ? edit[r.id] : (r.short_name || r.suggested || '');
            const dirty = val !== (r.short_name || '');
            return (
              <div key={r.id} style={{
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
                padding: '10px 12px', display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr) auto',
                gap: 10, alignItems: 'center',
              }}>
                <div style={{ minWidth: 0, color: 'var(--muted)', fontSize: 12.5,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                     title={r.name}>
                  {r.name}
                </div>
                <input value={val}
                  onChange={e => setEdit(x => ({ ...x, [r.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') save(r, val); }}
                  placeholder={r.suggested || t('короткое имя')}
                  style={{ ...INP, borderColor: dirty ? 'var(--accent)' : 'var(--border)' }} />
                <button onClick={() => save(r, val)} disabled={!dirty} style={{
                  background: dirty ? '#22c55e' : 'var(--card)', border: 'none', borderRadius: 8,
                  color: dirty ? '#fff' : 'var(--dim)', padding: '8px 14px', fontSize: 13,
                  fontWeight: 700, cursor: dirty ? 'pointer' : 'default', fontFamily: 'inherit',
                }}>✓</button>
              </div>
            );
          })}
          {data.count > data.results.length && (
            <div style={{ color: 'var(--dim)', fontSize: 12.5, padding: '8px 2px' }}>
              Показаны первые {data.results.length} из {data.count}. Уточните поиск.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TAB_TITLE = {
  dsp:   '🪵 ДСП',
  paper: '📄 Бумага и плёнка',
  ldsp:  '📦 ЛДСП',
};
// Код 1С впереди названия: менеджер и технолог в 1С ищут именно им, и он в
// пять раз короче названия — глаз цепляется первым.
// Сколько строк рисуем за раз. Сто было мало до папок: без них весь
// справочник шёл одним списком. В папке позиций сотни, а не тысячи.
const SHOWN_MAX = 300;

const CODE_ST = {
  color: 'var(--muted)', fontFamily: 'ui-monospace, monospace',
  fontSize: 12, fontWeight: 700, marginRight: 9,
};

// Завод-изготовитель. Раньше он приезжал из 1С в поле артикула и вставал в
// списке туда, где глаз ждёт код: столбец ломался, а строка притворялась не
// тем, чем была. Теперь — своим значком после названия.
// Строка списка — ровно одна линия, и она не переносится.
//
// Раньше строка была flex-wrap: у позиции с длинным рецептом («ДСП некондиция
// 2750*1830*16 мм, 1 сорт × 1 + Адриатика Импрегнированная бумага 85 г/кв.м
// 2760*1840 × 2») кнопки уезжали на второй ряд, у соседней оставались справа.
// Список из 688 позиций от этого выглядел рваным, и глазу не за что было
// зацепиться: кнопка «Удалить» каждый раз в новом месте.
const ROW_ST = {
  background: 'var(--bg)', borderRadius: 8, padding: '8px 12px',
  display: 'flex', alignItems: 'center', gap: 10,
};
const ROW_LEFT = {
  flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0,
  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
};
// Кнопки одинаковой ширины и всегда на месте — колонка справа получается
// прямой, и рука находит нужную не глядя.
const ACT_ST = {
  background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7,
  color: 'var(--muted)', width: 30, height: 28, fontSize: 13,
  cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
  display: 'grid', placeItems: 'center', padding: 0,
};

const MAKER_ST = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 5, padding: '1px 7px', marginLeft: 8,
  fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap',
};

// Таблица номенклатуры. Раньше строка была flex-линией: код, название, завод,
// ед. и папка слипались в одну ленту, толщины не было вовсе, а искать позиции
// «без толщины» приходилось глазами. Теперь каждое значение — своя колонка,
// по заголовку сортируется, толщина правится прямо в ячейке.
const NT_TH = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11.5, fontWeight: 700,
  color: 'var(--muted)', whiteSpace: 'nowrap', cursor: 'pointer',
  userSelect: 'none', borderBottom: '1px solid var(--border)',
  position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1,
};
const NT_TD = {
  padding: '6px 10px', fontSize: 13, color: 'var(--text)',
  borderBottom: '1px solid var(--border)', verticalAlign: 'middle',
};

const ntEmpty = (v) => v === null || v === undefined || v === '';

// Пустые значения всегда в конце — при любом направлении, иначе при Я-А
// технолог видел бы сверху сотни пустых строк.
function ntSort(rows, col, dir) {
  if (!col) return rows;
  const num = col.num;
  const out = rows.map(r => {
    const v = col.get(r);
    return { r, v: ntEmpty(v) ? null : (num ? Number(v) : String(v)) };
  });
  out.sort((a, b) => {
    if (a.v === null && b.v === null) return 0;
    if (a.v === null) return 1;
    if (b.v === null) return -1;
    const c = num ? a.v - b.v
      : a.v.localeCompare(b.v, 'ru', { numeric: true });
    return dir === 'desc' ? -c : c;
  });
  return out.map(x => x.r);
}

function ThicknessCell({ nom, onSave }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState('');
  // Enter и следом blur не должны сохранять дважды, а Esc — сохранять вовсе.
  const skip = useRef(false);
  const has = !ntEmpty(nom.thickness);
  const start = () => {
    skip.current = false;
    setVal(has ? String(Number(nom.thickness)) : '');
    setEdit(true);
  };
  const commit = () => {
    if (skip.current) return;
    skip.current = true;
    setEdit(false);
    const s = val.trim().replace(',', '.');
    const next = s === '' ? null : Number(s);
    if (next !== null && !isFinite(next)) return;
    const prev = has ? Number(nom.thickness) : null;
    if (next === prev) return;
    onSave(nom, next);
  };
  if (edit) {
    return (
      <input autoFocus type="number" step="0.1" min="0" value={val}
        onChange={e => setVal(e.target.value)} onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') { skip.current = true; setEdit(false); }
        }}
        style={{ width: 64, padding: '3px 6px', fontSize: 13,
                 background: 'var(--bg)', border: '1px solid var(--accent)',
                 borderRadius: 6, color: 'var(--text)', fontFamily: 'inherit' }} />
    );
  }
  return (
    <span onClick={start} title={t('Нажмите, чтобы указать толщину')}
      style={{
        display: 'inline-block', minWidth: 44, textAlign: 'center',
        padding: '2px 8px', borderRadius: 6, cursor: 'text',
        background: has ? 'transparent' : '#facc1530',
        border: '1px ' + (has ? 'solid transparent' : 'dashed #facc1580'),
        color: has ? 'var(--text)' : 'var(--muted)',
      }}>
      {has ? Number(nom.thickness) : '—'}
    </span>
  );
}

// rows — уже отфильтрованный список; сортировка идёт по нему целиком и только
// потом обрезается до limit, иначе «первые 300» были бы случайными.
function NomTable({ rows, ldsp, limit, cats, getRecipe, onThickness, onCategory,
                    onDelete, onParams, onEditRecipe, onRemoveRecipe }) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const cols = [
    { key: 'code', title: 'Код 1С', get: n => n.code_1c },
    { key: 'name', title: 'Наименование', get: n => n.name },
    { key: 'thick', title: 'Толщина, мм', get: n => n.thickness, num: true },
    { key: 'maker', title: 'Производитель', get: n => n.manufacturer },
    { key: 'unit', title: 'Ед.', get: n => n.unit },
    { key: 'folder', title: 'Папка', get: n => n.folder_path || n.folder_name },
    { key: 'cat', title: 'Категория', get: n => n.category_display },
    ...(ldsp ? [
      { key: 'matrix', title: 'Декор / матрица', get: n => n.matrix_name },
      { key: 'tone', title: 'Тон', get: n => n.color_tone },
      { key: 'recipe', title: 'Рецепт', num: true,
        get: n => { const r = getRecipe(n.id); return r ? (r.items || []).length : 0; } },
    ] : []),
  ];
  const cur = cols.find(c => c.key === sort.key);
  const sorted = ntSort(rows, cur, sort.dir);
  // 1-е нажатие — А-Я, 2-е — Я-А, 3-е снимает сортировку.
  const click = (key) => setSort(s => (
    s.key !== key ? { key, dir: 'asc' }
      : s.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' }));
  const shown = limit ? sorted.slice(0, limit) : sorted;
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%', maxHeight: '72vh',
                  overflowY: 'auto', border: '1px solid var(--border)',
                  borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c.key} style={NT_TH} onClick={() => click(c.key)}>
                {c.title}{sort.key === c.key && (sort.dir === 'desc' ? ' ▾' : ' ▴')}
              </th>
            ))}
            <th style={{ ...NT_TH, cursor: 'default' }} />
          </tr>
        </thead>
        <tbody>
          {shown.map(nom => {
            const rec = ldsp ? getRecipe(nom.id) : null;
            return (
              <tr key={nom.id}>
                <td style={{ ...NT_TD, ...CODE_ST, marginRight: 0, whiteSpace: 'nowrap' }}>
                  {nom.code_1c || ''}
                </td>
                <td style={{ ...NT_TD, fontWeight: 600, minWidth: 220 }}>{nom.name}</td>
                <td style={NT_TD}><ThicknessCell nom={nom} onSave={onThickness} /></td>
                <td style={{ ...NT_TD, color: 'var(--muted)' }}>{nom.manufacturer || ''}</td>
                <td style={{ ...NT_TD, color: 'var(--dim)', whiteSpace: 'nowrap' }}>{nom.unit}</td>
                <td style={{ ...NT_TD, color: 'var(--dim)', fontSize: 11.5 }}>
                  {nom.folder_path || nom.folder_name || ''}
                </td>
                <td style={NT_TD}>
                  {/* Вид товара правят редко, но только здесь: список и
                      показывает категорию, и меняет её. */}
                  <select value={nom.category || ''}
                    onChange={e => onCategory(nom, e.target.value)}
                    title={t('Что это за товар')}
                    style={{
                      background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 7, color: 'var(--dim)', padding: '4px 4px',
                      fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                    {cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </td>
                {ldsp && (
                  <>
                    <td style={NT_TD}>{nom.matrix_name || ''}</td>
                    <td style={NT_TD}>{nom.color_tone || ''}</td>
                    <td style={NT_TD}>
                      {rec ? (
                        <span title={(rec.items || []).map(
                            i => `${i.material_name} × ${Number(i.quantity)}`).join(',  ')}
                          style={{ background: '#22c55e18', color: '#22c55e', borderRadius: 5,
                                   padding: '2px 8px', fontSize: 11, fontWeight: 700,
                                   whiteSpace: 'nowrap' }}>
                          ✓ рецепт · {(rec.items || []).length}
                        </span>
                      ) : (
                        <span style={{ background: '#ef444418', color: '#ef4444', borderRadius: 5,
                                       padding: '2px 8px', fontSize: 11, fontWeight: 700,
                                       whiteSpace: 'nowrap' }}>
                          нет рецепта
                        </span>
                      )}
                    </td>
                  </>
                )}
                <td style={NT_TD}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {ldsp && (
                      <button onClick={() => onParams(nom)}
                        title={nom.press_temperature
                          ? `Режим прессования: ${nom.press_temperature}`
                          : t('Задать параметры прессования')}
                        style={{
                          ...ACT_ST,
                          background: nom.press_temperature ? '#f59e0b18' : 'var(--card)',
                          borderColor: nom.press_temperature ? '#f59e0b40' : 'var(--border)',
                          color: nom.press_temperature ? '#f59e0b' : 'var(--muted)',
                        }}>
                        ⚙
                      </button>
                    )}
                    {ldsp && (rec ? (
                      <>
                        <button onClick={() => onEditRecipe(nom)}
                          title={t('Изменить рецепт')}
                          style={{ ...ACT_ST, background: 'var(--accent-weak)',
                                   borderColor: 'var(--accent-weak)',
                                   color: 'var(--accent)' }}>
                          ✏
                        </button>
                        <button onClick={() => onRemoveRecipe(nom, rec)}
                          title={t('Снять рецепт')} style={ACT_ST}>
                          🧾
                        </button>
                      </>
                    ) : (
                      <span style={{ width: 66, flexShrink: 0 }} />
                    ))}
                    <button onClick={() => onDelete(nom)}
                      title={t('Снять с учёта — из базы не стирается')}
                      style={{ ...ACT_ST, color: '#ef4444', borderColor: '#ef444440' }}>
                      ⊘
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ADD_TAB = { dsp: 'add_dsp', paper: 'add_paper', ldsp: 'add_ldsp' };

// Категория выбирается внутри, а не тремя разными плитками снаружи.
//
// В справочниках стояли «ЛДСП», «ДСП» и «Бумага и плёнка» — три двери в один
// и тот же экран. Чтобы из плит перейти к плёнке, надо было выйти к плиткам и
// зайти заново; а сами три строки занимали треть списка справочников и
// выглядели как три разных дела.
function NomenclatureSection({ tab: initialTab = 'dsp' }) {
  const [tab, setTab] = useState(initialTab);
  const [nomenclatures, setNomenclatures] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [finishedProducts, setFinishedProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'add_paper' | 'add_dsp' | 'add_ldsp'
  // Папки справочника из 1С. Не выгружают — колонки нет, список как раньше.
  const folders = useFolders();
  const [folderId, setFolderId] = useState(null);
  // Снятые с учёта. Держим отдельным списком, а не подмешиваем: в обычной
  // работе они только мешают, а ищут их редко и намеренно.
  const [showGone, setShowGone] = useState(false);
  const [gone, setGone] = useState([]);
  useEffect(() => {
    warehouseAPI.getNomenclatures({ inactive: 'only' })
      .then(r => setGone(r.data.results || r.data || []))
      .catch(() => setGone([]));
  }, [nomenclatures.length]);
  const [saving, setSaving] = useState(false);

  // --- Add form state ---
  const initForm = (type, category, unit) => ({
    name: '', type, category, unit, min_stock: '0', description: '',
    matrix: '', press_temperature: '', press_hold_seconds: '', press_pressure: '',
    color_tone: '',
  });
  const [matrices, setMatrices] = useState([]);
  const [paramsNom, setParamsNom] = useState(null);   // ⚙ параметры модали
  const [search, setSearch] = useState('');            // в ЛДСП больше тысячи строк
  const [onlyNoThick, setOnlyNoThick] = useState(false);
  const [form, setForm] = useState(initForm('raw', 'paper', 'м2'));

  // --- Recipe editor state ---
  const [recipeProduct, setRecipeProduct] = useState(null);
  const [recipeItems, setRecipeItems] = useState([{ material: '', quantity: '1' }]);

  const STY = {
    input: { width: '100%', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
    select: { width: '100%', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
    label: { display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nomRes, rawRes, finRes, recRes] = await Promise.all([
        warehouseAPI.getNomenclatures(),
        warehouseAPI.getRawMaterials(),
        warehouseAPI.getFinishedProducts(),
        salesAPI.getRecipes(),
      ]);
      warehouseAPI.getTextures().then(r => setMatrices(r.data)).catch(() => {});
      setNomenclatures(nomRes.data);
      setRawMaterials(rawRes.data);
      setFinishedProducts(finRes.data);
      setRecipes(recRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Тип сменили слева — возвращаемся к списку и чистим форму добавления
  useEffect(() => {
    setActiveTab('list');
    setRecipeProduct(null);
    setSearch('');
  }, [tab]);


  const getRecipeForProduct = (productId) => recipes.find(r => r.product === productId);

  // --- Save new nomenclature ---
  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t("Введите название")); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        category: form.category,
        unit: form.unit,
        min_stock: parseFloat(form.min_stock) || 0,
        description: form.description.trim(),
        is_active: true,
      };
      // Пресс параметрлари — фақат тайёр маҳсулот учун
      if (form.type === 'finished') {
        // Пресс параметры — текстовые (CharField): допускают «/», диапазоны, точки.
        // Отправляем строку как есть (Number() ломал значения вида «180/200» → NaN → null).
        payload.matrix             = form.matrix ? Number(form.matrix) : null;
        payload.color_tone = (form.color_tone === '' || form.color_tone == null)
          ? null : Number(form.color_tone);
        payload.press_temperature  = String(form.press_temperature ?? '').trim();
        payload.press_hold_seconds = String(form.press_hold_seconds ?? '').trim();
        payload.press_pressure     = String(form.press_pressure ?? '').trim();
      }
      const { data } = await warehouseAPI.createNomenclature(payload);

      // Agar LDSP bo'lsa va recipeItemlar kiritilgan bo'lsa — retsept yaratish
      if (form.type === 'finished' && recipeItems.length > 0 && recipeItems[0].material) {
        try {
          await salesAPI.createRecipe({
            product: data.id,
            items: recipeItems.filter(r => r.material).map(r => ({
              material: parseInt(r.material),
              quantity: parseFloat(r.quantity) || 1,
            })),
            is_active: true,
          });
          toast.success(`✅ ${data.name} создан с рецептом`);
        } catch (err) {
          toast.success(`✅ ${data.name} создан, но рецепт не сохранён: ${err.response?.data?.detail || err.response?.data?.error || 'ошибка'}`);
        }
      } else {
        toast.success(`✅ ${data.name} создан`);
      }

      // Reset
      setForm(initForm('raw', 'paper', 'м2'));
      setRecipeItems([{ material: '', quantity: '1' }]);
      setActiveTab('list');
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.response?.data?.name?.[0] || 'Ошибка';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setSaving(false); }
  };

  // --- Delete nomenclature ---
  // Кнопка называлась «Удалить», а на деле снимала позицию с учёта. Человек
  // жал её, думая, что стирает строку насовсем, — и не понимал, что произошло.
  //
  // Насовсем стирать и нельзя: за позицией стоят рецепты, заказы и выпущенные
  // по ней листы. Поэтому действие оставлено как есть, а названо тем, что оно
  // делает, и рядом появился способ вернуть снятое обратно.
  const handleDelete = async (nom) => {
    if (!await confirmToast(
      `«${nom.name}» пропадёт из справочника и из выбора у менеджера.\n\n`
      + 'Из базы позиция не стирается: рецепты, заказы и выпущенные по ней '
      + 'листы остаются. Вернуть можно кнопкой «Снятые с учёта» вверху списка.',
      { title: 'Снять с учёта', danger: true, confirmText: 'Снять с учёта' })) return;
    try {
      await warehouseAPI.updateNomenclature(nom.id, { is_active: false });
      toast.success(t('Снято с учёта'));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  // Толщина правится в ячейке. Список не перезагружаем: на тысячу позиций это
  // лишний круг, а сортировка и прокрутка сбились бы. Меняем одну запись.
  const saveThickness = async (nom, value) => {
    try {
      const r = await warehouseAPI.updateNomenclature(nom.id, { thickness: value });
      const got = r && r.data && r.data.thickness !== undefined ? r.data.thickness : value;
      setNomenclatures(list => list.map(n => n.id === nom.id ? { ...n, thickness: got } : n));
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.thickness?.[0] || d?.detail || 'Ошибка');
    }
  };

  const handleRestore = async (nom) => {
    try {
      await warehouseAPI.updateNomenclature(nom.id, { is_active: true });
      toast.success(t('Вернули в справочник'));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  // Категория решает, в каком списке товар лежит и с какого склада цех спишет
  // материал. Её проставляет правило по названию, и оно ошибается: «Виридис
  // Импрегнированная бумага» попала в ЛДСП, потому что 1С завела её как
  // готовую продукцию. Правка здесь же, в строке, — идти за этим в другой
  // раздел никто не станет.
  const CATS = [
    { key: 'ldsp',  label: 'ЛДСП' },
    { key: 'dsp',   label: 'ДСП' },
    { key: 'paper', label: 'Бумага и плёнка' },
    { key: 'other', label: 'Прочее' },
  ];
  const setCategory = async (nom, cat) => {
    if (!cat || cat === nom.category) return;
    const to = (CATS.find(c => c.key === cat) || {}).label || cat;
    const ok = await confirmToast(
      `Перенести «${nom.name}» в «${to}»?\n\n`
      + 'Из этого списка позиция исчезнет и появится в другом. '
      + 'Категория ещё решает, с какого склада цех спишет материал.',
      { confirmText: 'Перенести' });
    if (!ok) return;
    try {
      await warehouseAPI.updateNomenclature(nom.id, { category: cat });
      toast.success(`${t('Перенесено в')} «${to}»`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Ошибка');
    }
  };

  // Рецепт заводят в разделе «Рецепты»: там подбор по толщине и декору и поиск
  // по справочнику. Здесь его только снимают — снятый товар сам всплывает там
  // под фильтром «без рецепта».
  const removeRecipe = async (nom, rec) => {
    const list = (rec.items || [])
      .map(i => `${i.material_name} \u00d7 ${Number(i.quantity)}`).join(', ');
    const ok = await confirmToast(
      `Снять рецепт с «${nom.name}»?\n\nСейчас: ${list || 'пусто'}.\n`
      + 'Товар попадёт в «Рецепты» под фильтр «без рецепта» — там его заведут заново. '
      + 'Уже списанное сырьё не изменится.',
      { danger: true, confirmText: 'Снять рецепт' });
    if (!ok) return;
    try {
      await salesAPI.deleteRecipe(rec.id);
      toast.success(t('Рецепт снят — заведите его в разделе «Рецепты»'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Ошибка');
    }
  };

  const addRecipeRow = () => setRecipeItems(prev => [...prev, { material: '', quantity: '1' }]);
  const removeRecipeRow = (idx) => setRecipeItems(prev => prev.filter((_, i) => i !== idx));
  const updateRecipeRow = (idx, field, value) => {
    setRecipeItems(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'add_paper')  setForm(initForm('raw', 'paper', 'м2'));
    if (key === 'add_dsp')    setForm(initForm('raw', 'dsp', 'лист'));
    if (key === 'add_ldsp') {
      setForm(initForm('finished', 'ldsp', 'лист'));
      setRecipeItems([{ material: '', quantity: '1' }]);
    }
    setRecipeProduct(null);
  };

  if (loading) {
    return (
      <>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700, marginBottom: 14 }}>
        
        </h2>
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>
      </>
    );
  }

  const hit = (n) => !search.trim()
    || makeMatcher(search)(n.name, n.code_1c, n.code, n.manufacturer, n.description);
  // Раскладываем по КАТЕГОРИИ, а не по типу. 1С заводит всё подряд как
  // «готовую продукцию»: из 1241 позиции тип raw стоит у 29, а по сути сырья
  // куда больше. По типу в списке ЛДСП оказывались 602 чужие позиции —
  // импрегнированная бумага, МДФ, ХДФ, столешницы. Категория проставлена по
  // названию и говорит правду о том, что это за товар.
  // Поиск идёт по всему справочнику, мимо выбранной папки: технолог ищет
  // позицию, а не место, где она лежит.
  const inSel = (n) => !!search.trim() || inFolder(n, folderId, folders);
  const noThick = (n) => !onlyNoThick || n.thickness === null
    || n.thickness === undefined || n.thickness === '';
  const byCat = (cat) => nomenclatures.filter(
    n => n.category === cat && hit(n) && inSel(n) && noThick(n));
  const rawNoms = byCat(tab === 'paper' ? 'paper' : 'dsp');
  const finNoms = byCat('ldsp');
  // Всё, что не легло ни в одну категорию, показываем в ЛДСП отдельной группой:
  // спрятать позицию совсем — значит потерять её из виду насовсем.
  const otherNoms = tab === 'ldsp' ? byCat('other') : [];

  return (
    <div style={{ padding: 0 }}>
      {/* Тип выбран слева — здесь только его название, поиск и «Добавить» */}
      {/* Вид товара, поиск и «Добавить» прилипают к верху: позиций под тысячу,
          и прокрутив до середины списка, человек терял и поиск, и переключение
          вида — приходилось возвращаться наверх ради одного щелчка. */}
      {(
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: 16, flexWrap: 'wrap', gap: 12,
                      position: 'sticky', top: 58, zIndex: 30,
                      background: 'var(--card)', padding: '10px 0' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['ldsp', 'dsp', 'paper'].map(k => (
              <button key={k} onClick={() => { setTab(k); setActiveTab('list'); }}
                style={{
                  background: tab === k ? 'var(--accent)' : 'var(--bg)',
                  border: '1px solid ' + (tab === k ? 'var(--accent)' : 'var(--border)'),
                  borderRadius: 8, color: tab === k ? '#fff' : 'var(--muted)',
                  padding: '7px 14px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                {TAB_TITLE[k]}
                {tab === k && ` (${k === 'ldsp' ? finNoms.length : rawNoms.length})`}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('Поиск по названию или коду…')}
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
                       color: 'var(--text)', padding: '7px 12px', fontSize: 13, width: 240,
                       fontFamily: 'inherit' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={onlyNoThick}
                onChange={e => setOnlyNoThick(e.target.checked)} />
              Только без толщины
            </label>
            {activeTab === 'list' && gone.length > 0 && (
              <button onClick={() => setShowGone(v => !v)}
                title={t('Позиции, снятые с учёта — их можно вернуть')}
                style={{
                  background: showGone ? 'var(--accent-weak)' : 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  color: showGone ? 'var(--accent)' : 'var(--muted)',
                  padding: '7px 14px', fontSize: 13, cursor: 'pointer',
                  fontWeight: 600, fontFamily: 'inherit',
                }}>
                Снятые с учёта · {gone.length}
              </button>
            )}
            {activeTab === 'list' ? (
              <button onClick={() => handleTabChange(ADD_TAB[tab])} style={{
                background: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 8,
                color: '#fff', padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
              }}>+ Добавить</button>
            ) : (
              <button onClick={() => handleTabChange('list')} style={{
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
                color: 'var(--muted)', padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
              }}>← К списку</button>
            )}
          </div>
        </div>
      )}

      {/* --- LIST VIEW --- */}
      {activeTab === 'list' && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        {!search.trim() && (
          <CatalogTree folders={folders} value={folderId} onChange={setFolderId}
                       total={nomenclatures.length}
                       noFolder={nomenclatures.filter(n => !n.folder).length}
                       style={{ maxHeight: '70vh', position: 'sticky', top: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex',
                      flexDirection: 'column', gap: 16 }}>
          {showGone && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10,
                          padding: 12, background: 'var(--bg)' }}>
              <div style={{ color: 'var(--muted)', fontSize: 12.5,
                            marginBottom: 8, lineHeight: 1.5 }}>
                <b style={{ color: 'var(--text)' }}>Снятые с учёта — {gone.length}</b>
                {'. '}Их не видно ни в справочнике, ни в выборе товара. Из базы
                они не стёрты: рецепты и выпущенные по ним листы на месте.
                {gone.some(n => n.inactive_by_1c) && (
                  <> Помеченные «нет в 1С» снял обмен: позиция пропала из
                  каталога бухгалтера. Вернёте — обмен её больше не тронет.</>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {gone.slice(0, 100).map(nom => (
                  <div key={nom.id} style={{ ...ROW_ST, opacity: .75 }}>
                    <div style={ROW_LEFT}>
                      {nom.code_1c ? <span style={CODE_ST}>{nom.code_1c}</span> : null}
                      <span style={{ color: 'var(--muted)', fontSize: 13.5 }}>{nom.name}</span>
                      {nom.manufacturer ? <span style={MAKER_ST}>{nom.manufacturer}</span> : null}
                      {nom.inactive_by_1c ? (
                        <span style={{ ...MAKER_ST, color: 'var(--muted)' }}>
                          нет в 1С
                        </span>
                      ) : null}
                    </div>
                    <button onClick={() => handleRestore(nom)}
                      style={{
                        background: 'var(--accent-weak)', border: '1px solid var(--accent-weak)',
                        borderRadius: 7, color: 'var(--accent)', padding: '5px 12px',
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        fontFamily: 'inherit', flexShrink: 0,
                      }}>
                      Вернуть
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Сырьё выбранного типа */}
          {tab !== 'ldsp' && (
          <div>
            <NomTable key={tab} rows={rawNoms} ldsp={false} limit={0}
                cats={CATS} getRecipe={(id) => getRecipeForProduct(id)}
                onThickness={saveThickness} onCategory={setCategory}
                onDelete={handleDelete} onParams={setParamsNom}
                onEditRecipe={setRecipeProduct} onRemoveRecipe={removeRecipe}/>
            {rawNoms.length === 0 && <p style={{ color: 'var(--mid)', fontSize: 13, padding: '8px 0' }}>{t('Ничего не найдено')}</p>}
          </div>
          )}

          {/* Готовая продукция. Их больше тысячи — рисуем сотню, остальное
              достаётся поиском: длинный список всё равно листают не глазами. */}
          {tab === 'ldsp' && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <NomTable key="ldsp" rows={finNoms} ldsp limit={SHOWN_MAX}
                cats={CATS} getRecipe={(id) => getRecipeForProduct(id)}
                onThickness={saveThickness} onCategory={setCategory}
                onDelete={handleDelete} onParams={setParamsNom}
                onEditRecipe={setRecipeProduct} onRemoveRecipe={removeRecipe}/>
              {finNoms.length === 0 && otherNoms.length === 0 && <p style={{ color: 'var(--mid)', fontSize: 13, padding: '8px 0' }}>{t('Ничего не найдено')}</p>}
              {otherNoms.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 8 }}>
                    <b style={{ color: 'var(--warning)' }}>Без категории — {otherNoms.length}</b>
                    {'. '}Правило по названию не разобрало эти позиции. Пока они «прочее»,
                    цех не знает, с какого склада их списывать — укажите вид в списке справа.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {otherNoms.slice(0, 60).map(nom => (
                      <div key={nom.id} style={{
                        background: 'var(--bg)', borderRadius: 8, padding: '9px 14px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        gap: 10, flexWrap: 'wrap',
                      }}>
                        <span style={{ color: 'var(--text)', fontSize: 13.5 }}>{nom.name}</span>
                        <select value={nom.category || ''}
                          onChange={e => setCategory(nom, e.target.value)}
                          style={{
                            background: 'var(--card)', border: '1px solid var(--border)',
                            borderRadius: 7, color: 'var(--muted)', padding: '5px 8px',
                            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                          }}>
                          {CATS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                      </div>
                    ))}
                    {otherNoms.length > 60 && (
                      <p style={{ color: 'var(--dim)', fontSize: 12.5, padding: '6px 2px' }}>
                        Показаны первые 60 из {otherNoms.length}. Уточните поиск.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {finNoms.length > SHOWN_MAX && (
                <p style={{ color: 'var(--dim)', fontSize: 12.5, padding: '10px 2px' }}>
                  Показаны первые {SHOWN_MAX} из {finNoms.length} — откройте папку или уточните поиск.
                </p>
              )}
            </div>
          </div>
          )}
        </div>
        </div>
      )}

      {/* --- ADD PAPER --- */}
      {activeTab === 'add_paper' && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 18, marginBottom: 4 }}>{t('📄 Новая бумага / плёнка')}</div>

          <div>
            <label style={STY.label}>{t('Название')} <span style={{ color: '#ef4444' }}>*</span></label>
            <input style={STY.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t("Напр.: Белая Матт 70г")} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={STY.label}>{t('Ед. измерения')}</label>
              <select style={STY.select} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                <option value="м2">{t('м²')}</option>
                <option value="кг">{t('Кг')}</option>
                <option value="лист">{t('Листов')}</option>
                <option value="шт">{t('Штук')}</option>
              </select>
            </div>
            <div>
              <label style={STY.label}>{t('Мин. запас')}</label>
              <input type="number" style={STY.input} value={form.min_stock}
                onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))} />
            </div>
          </div>

          <div>
            <label style={STY.label}>{t('Описание')}</label>
            <textarea style={{ ...STY.input, resize: 'vertical', minHeight: 60 }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={t("Плотность, цвет, тип бумаги...")} />
          </div>

          <Btn variant="success" size="md" disabled={saving || !form.name.trim()} onClick={handleSave} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Сохранение...' : '✅ Сохранить'}
          </Btn>
        </div>
      )}

      {/* --- ADD DSP --- */}
      {activeTab === 'add_dsp' && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 18, marginBottom: 4 }}>{t('🪵 Новый ДСП')}</div>

          <div>
            <label style={STY.label}>{t('Название')} <span style={{ color: '#ef4444' }}>*</span></label>
            <input style={STY.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t("Напр.: ДСП 16мм")} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={STY.label}>{t('Ед. измерения')}</label>
              <select style={STY.select} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                <option value="лист">{t('Листов')}</option>
                <option value="м2">{t('м²')}</option>
                <option value="шт">{t('Штук')}</option>
              </select>
            </div>
            <div>
              <label style={STY.label}>{t('Мин. запас')}</label>
              <input type="number" style={STY.input} value={form.min_stock}
                onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))} />
            </div>
          </div>

          <div>
            <label style={STY.label}>{t('Описание (толщина и т.д.)')}</label>
            <textarea style={{ ...STY.input, resize: 'vertical', minHeight: 60 }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={t("Напр.: 16мм, шлифованный, E1...")} />
          </div>

          <Btn variant="success" size="md" disabled={saving || !form.name.trim()} onClick={handleSave} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Сохранение...' : '✅ Сохранить'}
          </Btn>
        </div>
      )}

      {/* --- ADD LDSP --- */}
      {activeTab === 'add_ldsp' && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 18, marginBottom: 4 }}>{t('📦 Новый ЛДСП')}</div>

          <div>
            <label style={STY.label}>{t('Название')} <span style={{ color: '#ef4444' }}>*</span></label>
            <input style={STY.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t("Напр.: ЛДСП Белый 16мм 1-ст")} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={STY.label}>{t('Ед. измерения')}</label>
              <select style={STY.select} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                <option value="лист">{t('Листов')}</option>
                <option value="м2">{t('м²')}</option>
                <option value="шт">{t('Штук')}</option>
              </select>
            </div>
            <div>
              <label style={STY.label}>{t('Мин. запас')}</label>
              <input type="number" style={STY.input} value={form.min_stock}
                onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))} />
            </div>
          </div>

          <div>
            <label style={STY.label}>{t('Описание')}</label>
            <textarea style={{ ...STY.input, resize: 'vertical', minHeight: 60 }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={t("Напр.: 1-сторонний, Белый Матт, 16мм...")} />
          </div>

          {/* Пресс параметрлари */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
            <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
              ⚙️ Параметры прессования
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={STY.label}>{t('Матрица (текстура)')}</label>
              <select style={STY.select} value={form.matrix}
                onChange={e => {
                  const val = e.target.value;
                  const t = matrices.find(x => String(x.id) === String(val));
                  // Матрица ўзгарганда параметрлар ҳар доим ўша матрицадан қайта тортилади
                  // (аввалги «фақат бўш майдонга» гуарди ном алмаштирилганда янгиланишни блоклаган эди).
                  setForm(f => t ? ({
                    ...f, matrix: val,
                    press_temperature:  t.press_temperature  ?? '',
                    press_hold_seconds: t.press_hold_seconds ?? '',
                    press_pressure:     t.press_pressure     ?? '',
                  }) : ({ ...f, matrix: val }));
                }}>
                <option value="">{t('— не указана —')}</option>
                {matrices.map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
              </select>
              <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4 }}>
                💡 При выборе матрицы её параметры подставятся автоматически.
                Новую матрицу можно добавить в разделе «Текстуры / Матрицы».
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                {/* Первое число — нижняя плита, второе — верхняя. Не
                    коридор: у каждой плиты своя цифра. */}
                <label style={STY.label}>{t('🌡 Температура: низ / верх (°C)')}</label>
                <input type="text" style={STY.input} value={form.press_temperature}
                  onChange={e => setForm(f => ({ ...f, press_temperature: e.target.value }))}
                  placeholder="190 / 195" />
              </div>
              <div>
                <label style={STY.label}>{t('⏱ Время (сек)')}</label>
                <input type="text" style={STY.input} value={form.press_hold_seconds}
                  onChange={e => setForm(f => ({ ...f, press_hold_seconds: e.target.value }))}
                  placeholder="300" />
              </div>
              <div>
                <label style={STY.label}>{t('💪 Давление (бар)')}</label>
                <input type="text" style={STY.input} value={form.press_pressure}
                  onChange={e => setForm(f => ({ ...f, press_pressure: e.target.value }))}
                  placeholder="25" />
              </div>
            </div>

            {/* Порядок в смене собирают от тёмного к светлому: плита приходит
                с пылью, на прессе копится грязь, и на тёмном ЛДСП её не
                видно — а к светлым пресс успевают протереть. Наоборот не
                работает: сор после светлой партии всплывёт на первом же
                тёмном листе.

                Пусто — тон угадывается по названию, и в заявке такая строка
                помечена пунктиром. Догадка не заменяет цифру: она показывает,
                где её ещё никто не поставил. */}
            <div style={{ marginTop: 12 }}>
              <label style={STY.label}>{t('🎨 Тон цвета (0 — чёрный, 100 — белый)')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input type="number" min={0} max={100}
                  style={{ ...STY.input, width: 110 }}
                  value={form.color_tone ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(f => ({ ...f, color_tone: v === '' ? '' : Math.max(0, Math.min(100, Number(v))) }));
                  }}
                  placeholder={t('не задан')} />
                {form.color_tone !== '' && form.color_tone != null && (
                  <span style={{
                    width: 30, height: 30, borderRadius: 6,
                    border: '1px solid var(--border)', flexShrink: 0,
                    background: `rgb(${Math.round(255 * Number(form.color_tone) / 100)},${Math.round(255 * Number(form.color_tone) / 100)},${Math.round(255 * Number(form.color_tone) / 100)})`,
                  }} />
                )}
                <span style={{ color: 'var(--dim)', fontSize: 11.5, lineHeight: 1.45 }}>
                  По нему собирается порядок смены: тёмные идут первыми, пока
                  пресс не протёрли. Пусто — тон угадается по названию.
                </span>
              </div>
            </div>
          </div>

          {/* Recipe items */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700 }}>
                🧾 Рецепт (опционально)
              </label>
              <span style={{ color: 'var(--dim)', fontSize: 11 }}>
                На 1 лист ЛДСП
              </span>
            </div>

            {recipeItems.map((ri, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <div style={{ flex: 3, minWidth: 0 }}>
                  <SearchSelect
                    options={rawMaterials.filter(m => m.type === 'raw')}
                    value={ri.material}
                    onChange={v => updateRecipeRow(idx, 'material', v)}
                    placeholder={t('— Материал —')} />
                </div>
                <input
                  type="number" step="0.01" min="0.01"
                  style={{ ...STY.input, flex: 1, minWidth: 70 }}
                  value={ri.quantity}
                  onChange={e => updateRecipeRow(idx, 'quantity', e.target.value)}
                  placeholder={t("Кол-во")}
                />
                {recipeItems.length > 1 && (
                  <button onClick={() => removeRecipeRow(idx)} style={{
                    background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, padding: '0 6px',
                  }}>✕</button>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button onClick={addRecipeRow} style={{
                background: 'transparent', border: '1px dashed var(--border)', borderRadius: 8,
                color: 'var(--muted)', padding: '8px 16px', fontSize: 13, cursor: 'pointer',
              }}>
                + Добавить материал
              </button>
            </div>

            <div style={{ marginTop: 10, color: 'var(--dim)', fontSize: 11 }}>
              💡 Рецепт можно добавить или изменить позже в списке номенклатуры.
            </div>
          </div>

          <Btn variant="success" size="md" disabled={saving || !form.name.trim()} onClick={handleSave} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Сохранение...' : '✅ Сохранить'}
          </Btn>
        </div>
      )}

      {/* --- ПАРАМЕТРЫ ПРЕССОВАНИЯ --- */}
      {paramsNom && (
        <ParamsModal
          nom={paramsNom}
          matrices={matrices}
          onClose={() => setParamsNom(null)}
          onSaved={() => { setParamsNom(null); load(); }}
        />
      )}

      {/* Правка рецепта — тем же окном, что и в разделе «Рецепты»: поиск по
          справочнику и любое число позиций. Заводят рецепт там, здесь только
          правят готовый и снимают. */}
      {recipeProduct && (
        <RecipeModal
          item={{ id: recipeProduct.id, name: recipeProduct.name,
                  dsp_suggested: '', film_suggested: '', film_options: [] }}
          recipe={getRecipeForProduct(recipeProduct.id)}
          dsps={rawMaterials.filter(m => m.category === 'dsp')}
          films={rawMaterials.filter(m => m.category === 'paper')}
          materials={rawMaterials}
          onClose={() => setRecipeProduct(null)}
          onSaved={() => { setRecipeProduct(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Текстуралар бошқаруви ─────────────────────────────────────────────────────
function TextureSection() {
  const [textures, setTextures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, code, description }
  const [saving, setSaving] = useState(false);

  // Yangi qo'shish form
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPT, setNewPT] = useState('');
  const [newHold, setNewHold] = useState('');
  const [newPress, setNewPress] = useState('');

  // Tahrirlash form
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [texPT, setTexPT] = useState('');
  const [texHold, setTexHold] = useState('');
  const [texPress, setTexPress] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    warehouseAPI.getTextures({ all: 'true' })
      .then(r => setTextures(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newName.trim() || !newCode.trim()) { toast.error(t("Название и код обязательны")); return; }
    setSaving(true);
    try {
      await warehouseAPI.createTexture({
        name: newName.trim(),
        code: newCode.trim().toUpperCase(),
        description: newDesc.trim(),
        press_temperature: newPT.trim(),
        press_hold_seconds: newHold.trim(),
        press_pressure: newPress.trim(),
        is_active: true,
      });
      toast.success(`Текстура "${newName.trim()}" добавлена`);
      setNewName(''); setNewCode(''); setNewDesc(''); setNewPT(''); setNewHold(''); setNewPress('');
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.code?.[0] || err.response?.data?.name?.[0] || err.response?.data?.detail || 'Ошибка');
    } finally { setSaving(false); }
  };

  const startEdit = (tex) => {
    setEditing(tex);
    setEditName(tex.name);
    setEditCode(tex.code);
    setEditDesc(tex.description || '');
    setTexPT(tex.press_temperature || '');
    setTexHold(tex.press_hold_seconds || '');
    setTexPress(tex.press_pressure || '');
  };

  const handleUpdate = async () => {
    if (!editing) return;
    if (!editName.trim() || !editCode.trim()) { toast.error(t("Название и код обязательны")); return; }
    setSaving(true);
    try {
      await warehouseAPI.updateTexture(editing.id, {
        name: editName.trim(),
        code: editCode.trim().toUpperCase(),
        description: editDesc.trim(),
        press_temperature: texPT.trim(),
        press_hold_seconds: texHold.trim(),
        press_pressure: texPress.trim(),
      });
      toast.success(t("Текстура обновлена"));
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.code?.[0] || err.response?.data?.detail || 'Ошибка');
    } finally { setSaving(false); }
  };

  const handleToggle = async (tex) => {
    try {
      await warehouseAPI.updateTexture(tex.id, { is_active: !tex.is_active });
      toast.success(tex.is_active ? 'Деактивирована' : 'Активирована');
      load();
    } catch (err) {
      // Голое «Ошибка» ничего не говорит человеку у экрана — показываем причину
      toast.error(err.response?.data?.detail || err.response?.data?.error
        || t('Не удалось изменить текстуру'));
    }
  };

  const INP = { width: '100%', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ padding: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>
        
        </h2>
        <Btn variant={adding ? 'ghost' : 'primary'} size="sm" onClick={() => { setAdding(!adding); setNewName(''); setNewCode(''); setNewDesc(''); }}>
          {adding ? '✕ Отмена' : '+ Добавить текстуру'}
        </Btn>
      </div>

      {/* Add form */}
      {adding && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                Название <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input style={INP} value={newName} onChange={e => setNewName(e.target.value)}
                placeholder={t("Напр.: Шагрень")} />
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                Код <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input style={INP} value={newCode} onChange={e => setNewCode(e.target.value)}
                placeholder={t("Напр.: SHG")} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5 }}>{t('Описание')}</label>
            <input style={INP} value={newDesc} onChange={e => setNewDesc(e.target.value)}
              placeholder={t("Необязательно")} />
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{t('⚙️ Параметры прессования')}</div>
            <div style={{ color: 'var(--dim)', fontSize: 11, marginBottom: 10 }}>
              При создании ЛДСП с этой матрицей значения подставятся автоматически. Можно писать через «/» или «.» — напр. 180/200.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('🌡 Температура (°C)')}</label>
                <input type="text" style={INP} value={newPT} onChange={e => setNewPT(e.target.value)} placeholder="180 / 200" />
              </div>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('⏱ Время (сек)')}</label>
                <input type="text" style={INP} value={newHold} onChange={e => setNewHold(e.target.value)} placeholder="300" />
              </div>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('💪 Давление (бар)')}</label>
                <input type="text" style={INP} value={newPress} onChange={e => setNewPress(e.target.value)} placeholder="25" />
              </div>
            </div>
          </div>
          <Btn variant="success" size="sm" disabled={saving || !newName.trim() || !newCode.trim()} onClick={handleAdd} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Сохранение...' : '✅ Сохранить'}
          </Btn>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20 }}>
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>{t('✏ Редактировать текстуру')}</h3>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('Название')}</label>
                <input style={INP} value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('Код')}</label>
                <input style={INP} value={editCode} onChange={e => setEditCode(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5 }}>{t('Описание')}</label>
                <input style={INP} value={editDesc} onChange={e => setEditDesc(e.target.value)} />
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{t('⚙️ Параметры прессования')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>🌡 °C</label>
                    <input type="text" style={INP} value={texPT} onChange={e => setTexPT(e.target.value)} placeholder="180 / 200" />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('⏱ сек')}</label>
                    <input type="text" style={INP} value={texHold} onChange={e => setTexHold(e.target.value)} placeholder="300" />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>{t('💪 бар')}</label>
                    <input type="text" style={INP} value={texPress} onChange={e => setTexPress(e.target.value)} placeholder="25" />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" size="md" onClick={() => setEditing(null)}>{t('Отмена')}</Btn>
              <Btn variant="success" size="md" onClick={handleUpdate} disabled={saving}>
                {saving ? 'Сохранение...' : '✅ Сохранить'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Texture list */}
      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20, fontSize: 13 }}>{t('Загрузка...')}</p>
      ) : textures.length === 0 ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20, fontSize: 14 }}>{t('Нет текстур')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {textures.map(tex => (
            <div key={tex.id} style={{
              background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              opacity: tex.is_active ? 1 : 0.5,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{tex.name}</span>
                <span style={{
                  background: 'var(--card)', borderRadius: 5, padding: '2px 7px',
                  fontSize: 12, color: 'var(--muted)', fontWeight: 700, fontFamily: 'monospace',
                }}>{tex.code}</span>
                {tex.description && (
                  <span style={{ color: 'var(--dim)', fontSize: 12 }}>{tex.description}</span>
                )}
                {!tex.is_active && (
                  <span style={{ background: '#ef444418', color: '#ef4444', borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>
                    Неактивна
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => startEdit(tex)} style={{
                  background: 'var(--accent-weak)', border: '1px solid var(--accent-weak)', borderRadius: 7,
                  color: 'var(--accent)', padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>✏</button>
                <button onClick={() => handleToggle(tex)} style={{
                  background: tex.is_active ? '#ef444415' : '#22c55e15',
                  border: `1px solid ${tex.is_active ? '#ef444430' : '#22c55e30'}`,
                  borderRadius: 7, padding: '5px 12px',
                  color: tex.is_active ? '#ef4444' : '#22c55e',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                  {tex.is_active ? 'Откл' : 'Вкл'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Пресс созламалари ──────────────────────────────────────────────────────────
function PressSettingsSection({ presses, onUpdate }) {
  const [editing, setEditing] = useState(null); // press object
  const [adding, setAdding]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [branches, setBranches] = useState([]);
  const [form, setForm]       = useState({ name: '', norm_per_hour: 60, is_active: true, branch: '' });
  const [addForm, setAddForm] = useState({ name: '', norm_per_hour: 60, branch: '' });

  // Цех, на который человек смотрит прямо сейчас (переключатель в шапке).
  // «all» значит «завод целиком» — тогда цех у пресса не подставить, его
  // придётся выбрать руками: станок стоит в одном конкретном цехе.
  const viewedBranch = sessionStorage.getItem('selected_branch') || 'all';

  useEffect(() => {
    branchAPI.getList().then(r => {
      const list = r.data || [];
      setBranches(list);
      // Умолчание для нового пресса: смотрим на цех — он и подставляется;
      // цех у человека всего один — тем более. Раньше здесь стояла единица
      // («Always Chimkent»), и пресс, заведённый из Алматы, уезжал в Шымкент.
      setAddForm(f => (f.branch ? f : {
        ...f,
        branch: viewedBranch !== 'all' ? Number(viewedBranch)
                : (list.length === 1 ? list[0].id : ''),
      }));
    }).catch(() => {});
  }, [viewedBranch]);

  const startEdit = (press) => {
    setEditing(press);
    setForm({
      name: press.name,
      norm_per_hour: press.norm_per_hour || 60,
      is_active: press.is_active !== false,
      branch: press.branch || '',
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!form.name.trim()) { toast.error(t("Название обязательно")); return; }
    if (!form.norm_per_hour || Number(form.norm_per_hour) < 1) { toast.error(t("Норма должна быть > 0")); return; }
    setSaving(true);
    try {
      await productionAPI.updatePress(editing.id, {
        name: form.name.trim(),
        norm_per_hour: Number(form.norm_per_hour),
        is_active: form.is_active,
        branch: form.branch || null,
      });
      toast.success(`${form.name.trim()} обновлён`);
      setEditing(null);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Ошибка');
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!addForm.name.trim()) { toast.error(t("Название обязательно")); return; }
    if (!addForm.norm_per_hour || Number(addForm.norm_per_hour) < 1) { toast.error(t("Норма должна быть > 0")); return; }
    setSaving(true);
    try {
      await productionAPI.createPress({
        name: addForm.name.trim(),
        norm_per_hour: Number(addForm.norm_per_hour),
        is_active: true,
        status: 'idle',
        branch: addForm.branch,
      });
      toast.success(`Пресс "${addForm.name.trim()}" создан`);
      setAddForm({ name: '', norm_per_hour: 60,
        branch: viewedBranch !== 'all' ? Number(viewedBranch)
                : (branches.length === 1 ? branches[0].id : '') });
      setAdding(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Ошибка');
    } finally { setSaving(false); }
  };

  const INP = { width: '100%', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ padding: 0 }}>
      {/* Add form */}
      {adding && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{t('🆕 Новый пресс')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                Название <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input style={INP} value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t("Напр.: Пресс №4")} />
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                Норма (лист/час) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input type="number" min="1" style={INP} value={addForm.norm_per_hour}
                onChange={e => setAddForm(f => ({ ...f, norm_per_hour: e.target.value }))} />
            </div>
          </div>
          {/* Цех спрашиваем, только когда их несколько: выбор из одного
              выглядит как забытая настройка. Пресс стоит в одном цехе, и
              угадывать этот цех за человека нельзя — раньше угадывали
              Шымкент, и пресс для Алматы уезжал туда же. */}
          {branches.length > 1 && (
            <div>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                Цех <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select style={INP} value={addForm.branch}
                onChange={e => setAddForm(f => ({ ...f, branch: e.target.value ? Number(e.target.value) : '' }))}>
                <option value="">— выберите цех —</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="success" size="sm"
              disabled={saving || !addForm.name.trim()
                        || (branches.length > 1 && !addForm.branch)}
              onClick={handleCreate}>
              {saving ? 'Сохранение...' : '✅ Создать'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => { setAdding(false); setAddForm({ name: '', norm_per_hour: 60 }); }}>
              Отмена
            </Btn>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Btn variant={adding ? 'ghost' : 'success'} size="sm" onClick={() => setAdding(!adding)}>
          {adding ? '✕ Отмена' : '+ Добавить пресс'}
        </Btn>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {presses.map(press => (
          <div key={press.id} style={{
            background: 'var(--bg)', borderRadius: 8, padding: '12px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            opacity: press.is_active !== false ? 1 : 0.5,
          }}>
            <div>
              <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{press.name}</span>
              <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 12 }}>
                Норма: <b style={{ color: 'var(--muted)' }}>{press.norm_per_hour} лист/час</b>
              </span>
              {/* Чей это цех — видно в строке, а не только внутри настройки:
                  именно из-за невидимого цеха все прессы оказались в одном. */}
              {branches.length > 1 && (
                <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 12 }}>
                  Цех: <b style={{ color: press.branch ? 'var(--muted)' : '#ef4444' }}>
                    {branches.find(b => b.id === press.branch)?.name || 'не указан'}
                  </b>
                </span>
              )}
              <span style={{
                background: PRESS_STATUS_CFG[press.status]?.color + '18' || 'var(--card)',
                color: PRESS_STATUS_CFG[press.status]?.color || 'var(--dim)',
                borderRadius: 5, padding: '2px 8px', marginLeft: 10, fontSize: 11, fontWeight: 600,
              }}>
                {PRESS_STATUS_CFG[press.status]?.label || press.status}
              </span>
              {press.is_active === false && (
                <span style={{ background: '#ef444418', color: '#ef4444', borderRadius: 5, padding: '2px 7px', marginLeft: 8, fontSize: 11, fontWeight: 600 }}>
                  Неактивен
                </span>
              )}
            </div>
            <button onClick={() => startEdit(press)} style={{
              background: 'var(--accent-weak)', border: '1px solid var(--accent-weak)', borderRadius: 7,
              color: 'var(--accent)', padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              ⚙ Настроить
            </button>
          </div>
        ))}
        {presses.length === 0 && !adding && (
          <p style={{ color: 'var(--mid)', fontSize: 14, textAlign: 'center', padding: 20 }}>
            Прессы не найдены. Нажмите «+ Добавить пресс»
          </p>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20 }}>
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>
                ⚙️ Настройка: {editing.name}
              </h3>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                  Название <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input style={INP} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                  Норма (листов/час) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input type="number" min="1" style={INP} value={form.norm_per_hour}
                  onChange={e => setForm(f => ({ ...f, norm_per_hour: e.target.value }))} />
                <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4 }}>
                  Средняя производительность пресса. Используется для расчёта OEE и планирования.
                </div>
              </div>

              {/* Цех правится здесь же: семь прессов уже стоят не в своём, и
                  чинить это иначе как через базу было нельзя. */}
              {branches.length > 1 && (
                <div>
                  <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5, fontWeight: 600 }}>
                    Цех <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select style={INP} value={form.branch || ''}
                    onChange={e => setForm(f => ({ ...f, branch: e.target.value ? Number(e.target.value) : '' }))}>
                    <option value="">— не указан —</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4 }}>
                    Пресс виден только своему цеху. Без цеха его не увидит никто.
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 500 }}>{t('Пресс активен')}</span>
                </label>
                <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4 }}>
                  Неактивные прессы скрываются из списков выбора.
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" size="md" onClick={() => setEditing(null)}>{t('Отмена')}</Btn>
              <Btn variant="success" size="md" onClick={handleSave} disabled={saving}>
                {saving ? 'Сохранение...' : '✅ Сохранить'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Состав заказа: что берём со склада, что прессуем ───────────
//
// Решение «разрешить со склада или всё-таки прессовать» принимают по листам, а
// не по номеру заказа. Раньше состав брали из поля `items` списка заказов —
// а его там нет вовсе: краткий сериализатор отдаёт только число позиций. Список
// всегда выходил пустым, и об этом ничто не сообщало.
function OrderBreakdown({ orderId }) {
  const [data, setData] = useState(null);   // null — ещё не знаем
  const [err, setErr]   = useState('');

  useEffect(() => {
    let alive = true;
    salesAPI.orderBreakdown(orderId)
      .then(r => { if (alive) setData(r.data); })
      // Ошибку пишем на месте, а не глотаем: пустое место вместо состава
      // человек прочтёт как «в заказе ничего нет».
      .catch(e => { if (alive) setErr(e.response?.data?.error || 'Не удалось загрузить состав'); });
    return () => { alive = false; };
  }, [orderId]);

  if (err) return (
    <div style={{ color: '#ef4444', fontSize: 13, padding: '6px 0' }}>{err}</div>
  );
  if (data === null) return (
    <div style={{ color: 'var(--dim)', fontSize: 13, padding: '6px 0' }}>{t('Загрузка')}…</div>
  );

  const items = data.items || [];
  const d = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : '');

  // Шапка — те же поля и в том же порядке, что в карточке заказа у менеджера.
  // Технолог и менеджер должны читать один и тот же заказ одинаково, иначе
  // они сверяют его по телефону.
  const head = [
    ['Клиент',  data.client_name],
    ['Принял',  data.created_by_name],
    ['Создан',  d(data.created_at)],
    ['Срок',    d(data.deadline)],
  ].filter(([, v]) => v);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 4 }}>
        {head.map(([label, value]) => (
          <div key={label} style={{ fontSize: 13 }}>
            <span style={{ color: 'var(--dim)' }}>{t(label)}: </span>
            <b style={{ color: 'var(--text)' }}>{value}</b>
          </div>
        ))}
      </div>

      {/* Заказ приняли, когда сырья не хватало. Технолог ставит такую позицию
          на пресс, не зная этого, — и пресс встаёт без плиты. */}
      {data.stock_warning && (
        <div style={{
          color: '#d97706', background: '#f59e0b12', border: '1px solid #f59e0b40',
          borderRadius: 8, padding: '6px 10px', fontSize: 12.5,
        }}>⚠ {t('Принят без остатка')}: {data.stock_warning}</div>
      )}
      {data.note && (
        <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>{data.note}</div>
      )}

      {!items.length && (
        <div style={{ color: 'var(--dim)', fontSize: 13 }}>{t('В заказе нет позиций')}</div>
      )}
      {items.map(it => (
        <div key={it.id} style={{
          display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
          fontSize: 13.5,
        }}>
          <span style={{ color: 'var(--text)', flex: 1, minWidth: 180 }}>
            {it.product_name}
          </span>
          <b style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
            {it.quantity} {it.unit}
          </b>
          {/* Два числа рядом и разным цветом — это и есть весь вопрос.
              Строка бывает и смешанной: часть лежит, часть делать. */}
          {it.from_stock > 0 && (
            <span style={{
              background: '#22c55e18', color: '#16a34a', border: '1px solid #22c55e50',
              borderRadius: 6, padding: '1px 9px', fontSize: 12.5, fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>📦 {t('со склада')} {it.from_stock}</span>
          )}
          {it.produce > 0 && (
            <span style={{
              background: '#f59e0b18', color: '#d97706', border: '1px solid #f59e0b50',
              borderRadius: 6, padding: '1px 9px', fontSize: 12.5, fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>🏭 {t('прессовать')} {it.produce}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Заказы, которые закрывает склад ───────────────────
//
// Прессовать нечего: всё лежит готовым. Раньше такой заказ вставал в общую
// очередь согласования и ждал технолога наравне с теми, что надо производить —
// технолог смотрел на него, не находил работы и пропускал. Заказ висел,
// клиент ждал, а товар всё это время стоял на складе.
//
// Отгрузить его молча тоже нельзя: заказ может быть не оплачен, клиент может
// быть в долгу, те же листы могут быть обещаны другому. Поэтому решение —
// здесь, у того, кто видит и цех, и остаток.
function StockOrders() {
  const [rows, setRows]   = useState([]);
  const [busy, setBusy]   = useState(null);
  const [loading, setLoading] = useState(true);
  // Заказ, по которому открыта выписка
  const [issueFor, setIssueFor] = useState(null);

  const load = useCallback(() => {
    salesAPI.getOrders({ status: 'stock' })
      .then(r => setRows(r.data.results || r.data || []))
      .catch(() => toast.error(t('Ошибка загрузки')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (order, kind) => {
    setBusy(order.id);
    try {
      if (kind === 'allow') {
        await salesAPI.allowShipment(order.id);
        toast.success(t('Отгрузка разрешена — заказ ушёл кладовщику'));
      } else {
        await salesAPI.orderToProduction(order.id);
        toast.success(t('Заказ отправлен в согласование с цехом'));
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(null); }
  };

  if (loading) return <div style={{ color: 'var(--dim)' }}>{t('Загрузка')}…</div>;

  if (!rows.length) return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 14, padding: '40px 20px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
      <div style={{ color: 'var(--text)', fontWeight: 700 }}>{t('Ждущих разрешения нет')}</div>
      <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 6, maxWidth: 520, margin: '6px auto 0' }}>
        Сюда попадают заказы, которые целиком закрываются складом: прессовать
        нечего, нужно только разрешить отгрузку.
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Выписка поверх экрана: решение принято здесь, и уводить человека на
          другую страницу, чтобы он там заново нашёл того же клиента и тот же
          заказ, — потерянная минута и лишний шанс промахнуться строкой. */}
      {issueFor && (
        <div onClick={() => setIssueFor(null)} style={{
          position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: 16, overflowY: 'auto',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg)', borderRadius: 14, padding: 16,
            maxWidth: 900, width: '100%', marginTop: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                          marginBottom: 12 }}>
              <b style={{ color: 'var(--text)', fontSize: 17, flex: 1 }}>
                {t('Разрешение на отгрузку')} · {issueFor.order_number}
              </b>
              <button onClick={() => setIssueFor(null)} style={{
                background: 'none', border: 'none', color: 'var(--dim)',
                fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <ShipmentNotes
              preset={{ clientId: issueFor.client, orderId: issueFor.id }}
              onIssued={() => { setIssueFor(null); load(); }}
            />
          </div>
        </div>
      )}

      {rows.map(o => (
        <div key={o.id} style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderLeft: '3px solid #0ea5e9', borderRadius: 14, padding: 16,
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text)', fontWeight: 800, fontSize: 16 }}>
              {o.order_number}
            </span>
            <span style={{ color: 'var(--text)', fontSize: 15 }}>{o.client_name}</span>
            {o.deadline && (
              <span style={{ color: 'var(--dim)', fontSize: 13 }}>
                {t('срок')} {new Date(o.deadline).toLocaleDateString('ru-RU')}
              </span>
            )}
            <span style={{
              marginLeft: 'auto', background: '#0ea5e918', color: '#0ea5e9',
              borderRadius: 20, padding: '3px 12px', fontSize: 12.5, fontWeight: 700,
            }}>📦 {t('всё есть на складе')}</span>
          </div>

          {/* Что именно уедет — видно здесь, а не в отдельном окне: решение
              принимают по составу, а не по номеру заказа. */}
          <div style={{ marginTop: 10 }}>
            <OrderBreakdown orderId={o.id} />
          </div>

          {o.note && (
            <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 8 }}>{o.note}</div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            {/* Разрешение и выписка — одно движение. Раньше кнопка только
                снимала запрет, и заказ повисал: разрешён, а документа для
                склада нет, и кладовщик о нём не знает. Теперь открывается
                готовая выписка — с этим заказом и проставленными
                количествами. Подтвердить, дописать или закрыть. */}
            <button onClick={() => setIssueFor(o)} disabled={busy === o.id}
              className="touch-btn" style={{
                background: '#22c55e', border: 'none', borderRadius: 10, color: '#fff',
                padding: '11px 22px', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>✅ {t('Разрешить и выписать')}</button>
            {/* Остаток на складе — не обещание: те же листы могут быть нужны
                под другой заказ или под пересорт. Тогда заказ идёт как все. */}
            <button onClick={() => act(o, 'prod')} disabled={busy === o.id}
              className="touch-btn" style={{
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 10, color: 'var(--muted)', padding: '11px 20px',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              }}>🏭 {t('Всё-таки прессовать')}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Настройка сортов качества ────────────────────────────────────────────────
function GradesSection() {
  const [grades, setGrades]   = useState([]);
  const [adding, setAdding]   = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving]   = useState(false);

  const loadGrades = useCallback(() => {
    gradeAPI.getGradeTypes().then(r => setGrades(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadGrades(); }, [loadGrades]);

  const handleToggle = async (grade) => {
    if (grade.is_default) return;
    try {
      await gradeAPI.updateGradeType(grade.id, { is_active: !grade.is_active });
      loadGrades();
      toast.success(grade.is_active ? 'Деактивирован' : 'Активирован');
    } catch { toast.error(t("Ошибка")); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await gradeAPI.createGradeType({
        name: newName.trim(), is_default: false, is_active: true,
        order: grades.length + 1,
      });
      setNewName(''); setAdding(false); loadGrades();
      toast.success(t("Тип добавлен"));
    } catch { toast.error(t("Ошибка сохранения")); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 0 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 16,
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>

        </h2>
        <Btn
          variant={adding ? 'ghost' : 'primary'}
          onClick={() => { setAdding(!adding); setNewName(''); }}
        >
          {adding ? '✕ Отмена' : '+ Добавить тип'}
        </Btn>
      </div>

      {adding && (
        <div style={{
          background: 'var(--bg)', borderRadius: 8, padding: 14,
          marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder={t("Название, напр.: 3-й сорт")}
            style={{
              flex: 1, padding: '8px 12px', background: 'var(--card)',
              border: '1px solid var(--border)', borderRadius: 8,
              color: 'var(--text)', fontSize: 14,
            }}
          />
          <Btn
            variant="success" size="md"
            disabled={!newName.trim() || saving}
            onClick={handleAdd}
          >
            {saving ? '...' : '✅ Сохранить'}
          </Btn>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {grades.map(grade => (
          <div key={grade.id} style={{
            background: 'var(--bg)', borderRadius: 8, padding: '12px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            opacity: grade.is_active ? 1 : 0.5,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: grade.is_active ? '#22c55e' : 'var(--mid)',
                display: 'inline-block', flexShrink: 0,
              }} />
              <span style={{ color: 'var(--text)', fontSize: 15, fontWeight: 600 }}>
                {grade.name}
              </span>
              {grade.is_default && (
                <span style={{
                  background: '#22c55e18', color: '#22c55e',
                  borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>{t('По умолчанию')}</span>
              )}
              {!grade.is_active && (
                <span style={{
                  background: 'var(--mid)15', color: 'var(--dim)',
                  borderRadius: 5, padding: '2px 8px', fontSize: 11,
                }}>{t('Неактивен')}</span>
              )}
            </div>
            {!grade.is_default && (
              <button
                onClick={() => handleToggle(grade)}
                style={{
                  background: grade.is_active ? '#ef444415' : '#22c55e15',
                  border: `1px solid ${grade.is_active ? '#ef444430' : '#22c55e30'}`,
                  borderRadius: 7, padding: '5px 14px',
                  color: grade.is_active ? '#ef4444' : '#22c55e',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {grade.is_active ? 'Деактивировать' : 'Активировать'}
              </button>
            )}
          </div>
        ))}
        {grades.length === 0 && (
          <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20, fontSize: 14 }}>
            Нет типов сортов
          </p>
        )}
      </div>

      <div style={{ marginTop: 12, color: 'var(--mid)', fontSize: 12 }}>
        💡 Активные типы отображаются в терминале контролёра качества как кнопки.
        Тип «По умолчанию» — это 1-й сорт (хорошие листы).
      </div>
    </div>
  );
}

// ─── Главная страница технолога ───────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
// ТАБЛИЦА СОГЛАСОВАНИЯ (Лист1)
//
// Раньше технолог согласовывал заказы по одному. Теперь — одной таблицей:
// все несогласованные позиции сразу, с прессом, порядком и примечанием.
// Порядок строк = порядок очереди у оператора, поэтому стрелками технолог
// выстраивает смену так, чтобы матрицу меняли пореже.
// ═══════════════════════════════════════════════════════════════════════

// Черновик таблицы согласования. Технолог набирает её долго, а один
// случайный клик по меню раньше стирал всё введённое. Держим в браузере до
// тех пор, пока заявку не согласуют или черновик не очистят руками.
const APPROVAL_DRAFT_KEY = 'lamtech_approval_draft_v1';

const readApprovalDraft = () => {
  try {
    const d = JSON.parse(localStorage.getItem(APPROVAL_DRAFT_KEY) || '{}');
    return (d && typeof d === 'object') ? d : {};
  } catch { return {}; }
};

const writeApprovalDraft = (draft) => {
  // Приватный режим или переполнение хранилища не должны ломать работу
  try { localStorage.setItem(APPROVAL_DRAFT_KEY, JSON.stringify(draft)); } catch { /* ignore */ }
};

const clearApprovalDraft = () => {
  try { localStorage.removeItem(APPROVAL_DRAFT_KEY); } catch { /* ignore */ }
};

// Итог по каждой матрице: сколько листов и сколько строк за ней стоит.
//
// Матрица в таблице может идти не одним куском — порядок строк технолог
// выстраивает стрелками сам. Поэтому считаем по ВСЕЙ видимой таблице, а не по
// куску: вопрос у полосы один — сколько работы в этой матрице всего.
// Дата приёма заказа: «05.10», год — только если не текущий
function fmtOrderDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return String(new Date().getFullYear()) === y ? `${d}.${m}` : `${d}.${m}.${y.slice(2)}`;
}

function textureTotals(rows) {
  const out = new Map();
  rows.forEach(r => {
    const key = r.texture_name || '—';
    const cur = out.get(key) || { qty: 0, rows: 0, marked: 0 };
    if (r.marked) {
      cur.marked += 1;
    } else {
      cur.qty += Number(r.quantity) || 0;
      cur.rows += 1;
    }
    out.set(key, cur);
  });
  return out;
}

export function ApprovalTable({ presses, onApproved }) {
  const [rows, setRows]         = useState([]);      // порядок = порядок в таблице
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  // Черновик поднимаем сразу при создании состояния — до первой загрузки
  const [sel, setSel]           = useState(() => new Set(readApprovalDraft().sel || []));
  const [fields, setFields]     = useState(() => readApprovalDraft().fields || {});
  // Продукция без рецепта. Технолог отправляет её в производство, не зная об
  // этом: ошибки нигде нет, смена работает, а в 1С уходит документ без
  // материалов — ни требования, ни себестоимости. Замечают через месяц.
  const [noRecipe, setNoRecipe] = useState({ count: 0, in_queue: 0, results: [] });
  const [showNoRecipe, setShowNoRecipe] = useState(false);
  // За день накапливается три десятка позиций, и нужную ищут глазами сверху
  // вниз. Отметить одного заказчика или один цвет без поиска — это прокрутка
  // всей таблицы и промахи по чужим строкам.
  const [q, setQ] = useState('');
  // Позиции, сделанные не в свою смену. Спрашиваем здесь, на экране, куда
  // технолог заходит каждый день: почтой такое теряется, а в отчёте за месяц
  // вспомнить уже нечего.
  const [mismatch, setMismatch] = useState([]);
  const loadMismatch = useCallback(() => {
    productionAPI.shiftMismatch()
      .then(r => setMismatch(r.data.results || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadMismatch(); }, [loadMismatch]);

  const resolveShift = async (row, action) => {
    try {
      await productionAPI.resolveShift(row.queue_item_id, action);
      toast.success(action === 'move'
        ? `Перенесено в смену «${row.fact_display}»`
        : `Оставлено в смене «${row.planned_display}»`);
      loadMismatch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ошибка');
    }
  };
  // Заказы, которые целиком закрываются складом.
  //
  // Их тут не было вовсе: заказ на товар, который лежит на складе, получает
  // статус «со склада» и уходит на отдельный экран — «Разрешение на отгрузку».
  // Планировщик планирует ЗДЕСЬ и на тот экран не заходит: для него такой
  // заказ просто пропадал. Менеджер принял, клиент ждёт, а решения нет.
  //
  // Показываем их здесь же, но ОТДЕЛЬНОЙ полосой, а не строками таблицы: в
  // таблице каждой строке назначают пресс и смену, а этим прессовать нечего.
  // Полоса называет состояние — «есть на складе» — и даёт ровно два решения,
  // которые тут возможны:
  //   «Разрешить и выписать» — прессовать нечего: разрешаем и тут же пишем
  //       складу распоряжение, кому и сколько выдать. Двумя действиями это
  //       разваливалось: разрешённый заказ без распоряжения повисал между
  //       кабинетом и складом, потому что склад грузит по документу;
  //   «Всё-таки прессовать»  — листы придержим, сделаем свежие. Заказ уходит
  //       в обычное согласование и появляется строкой в этой же таблице.
  //
  // Те же два решения и теми же словами стоят на экране «Разрешение на
  // отгрузку» у технолога. Здесь они появились потому, что планирует не он:
  // планировщик работает в этой таблице и на тот экран не заходит.
  const [stockOrders, setStockOrders] = useState([]);
  const [stockBusy, setStockBusy]     = useState(null);
  // Какой заказ раскрыт. Один за раз: полоса стоит над рабочей таблицей, и
  // раскрытые разом три заказа сдвинули бы её вниз с экрана.
  const [stockOpen, setStockOpen]     = useState(null);
  // Заказ, по которому открыта выписка распоряжения
  const [stockIssue, setStockIssue]   = useState(null);
  const loadStock = useCallback(() => {
    salesAPI.getOrders({ status: 'stock' })
      .then(r => setStockOrders(r.data.results || r.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadStock(); }, [loadStock]);

  // На какую смену пойдёт строка. Технолог один знает это заранее: он видит
  // и объём, и очередь. Раньше смену ставили часы в момент выписки — а
  // выписывают заявки днём, и «Ночь» не появлялась ни разу.
  const [shifts, setShifts] = useState({});   // order_item_id -> day|night

  // На какой день выписываем заявку. Пусто — на сегодня, как было всегда.
  //
  // Заявку пишут заранее: в пятницу на понедельник. До сих пор такая работа
  // падала прессу в очередь СРАЗУ, и оператор начинал её сегодня — сырьё
  // уходило под понедельник, а сегодняшний заказ вставал.
  const [planDate, setPlanDate] = useState('');

  // Убираем СТРОКУ ЗАКАЗА, а не только строку таблицы: заказ, в котором
  // осталась ошибочная позиция, не закроется никогда, а сама позиция вернётся
  // при следующем согласовании.
  // Порядок строк технолог выстраивает стрелками, поэтому фильтруем, а не
  // сортируем: найденное остаётся на своих местах в общей очереди.
  const shownRows = rows.filter(r => makeMatcher(q)(
    r.client_name, r.product_name, r.texture_name, r.order_number, r.thickness));
  // Сколько работы стоит за каждой матрицей. Решение «менять ли матрицу»
  // стоит смены и часа простоя, а объём под неё до сих пор приходилось
  // складывать глазами по строкам. Считаем по тому, что видно на экране:
  // поиск сузил таблицу — сузился и итог, иначе он отвечал бы не про неё.
  // Помеченные на удаление в счёт не идут — согласовать их всё равно нельзя.
  const texTotals = textureTotals(shownRows);

  const removeRow = async (r) => {
    const ok = await confirmToast(
      `Убрать «${r.product_name}» из заказа ${r.order_number}?

` +
      'Позиция исчезнет из заказа. Так убирают то, что записали по ошибке.',
      { confirmText: 'Убрать', danger: true });
    if (!ok) return;
    try {
      await productionAPI.removeOrderItem(r.order_item_id);
      toast.success(t('Позиция убрана'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось убрать');
    }
  };

  // Убирать позиции и закрывать заказы может только администратор: позиция
  // удаляется насовсем, закрытый заказ уходит из работы. Сервер проверяет
  // то же самое — здесь только не показываем кнопки, которые ответят 403.
  const isAdmin = sessionStorage.getItem('role') === 'admin';
  // Закрыть отмеченные заказы может и технолог — он и разбирает эту таблицу
  const canCloseSel = isAdmin || sessionStorage.getItem('role') === 'technolog';

  // Закрыть заказы отмеченных строк — так же, как «Закрыть» у менеджера,
  // но разом и без сообщения клиенту. Закрывается заказ целиком: уйдут и
  // его неотмеченные строки.
  const selOrders = new Set(rows.filter(r => sel.has(r.order_item_id))
                                .map(r => r.order_number));
  const [closingSel, setClosingSel] = useState(false);
  const closeSelected = async () => {
    const picked = rows.filter(r => sel.has(r.order_item_id));
    if (!picked.length) return;
    const nOrders = new Set(picked.map(r => r.order_number)).size;
    const nRows = rows.filter(r => selOrders.has(r.order_number)).length;
    const ok = await confirmToast(
      `Закрыть заказы отмеченных строк: ${nOrders} заказ(ов), ${nRows} строк в таблице?

` +
      'Заказы будут закрыты целиком, клиентам уведомление не уйдёт. ' +
      'Резервы сырья снимутся. Заказ, по которому есть работа в очереди ' +
      'пресса, не закроется.',
      { confirmText: `Закрыть ${nOrders}`, danger: true });
    if (!ok) return;
    setClosingSel(true);
    try {
      const { data } = await productionAPI.closeSelectedOrders(
        picked.map(r => r.order_item_id));
      const done = data.closed || [];
      const errs = data.errors || [];
      if (errs.length) {
        toast.error(`Закрыто: ${done.length}, не закрыто: ${errs.length}. ` +
          errs.slice(0, 3).map(e => `${e.order}: ${e.error}`).join('; '),
          { duration: 8000 });
      } else {
        toast.success(`Закрыто заказов: ${done.length}`);
      }
      const gone = new Set(done);
      const goneIds = new Set(rows.filter(r => gone.has(r.order_number))
                                  .map(r => r.order_item_id));
      setSel(prev => new Set([...prev].filter(id => !goneIds.has(id))));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось закрыть');
    } finally {
      setClosingSel(false);
    }
  };

  // Порядок строк технолог выстраивает стрелками руками, поэтому фоновое
  // обновление не может просто заменить массив — читаем текущий через ref.
  const rowsRef  = useRef([]);
  const inFlight = useRef(false);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const load = useCallback((opts = {}) => {
    const bg = !!opts.background;
    if (inFlight.current) return;          // не копим запросы поверх друг друга
    inFlight.current = true;
    if (!bg) setLoading(true);
    productionAPI.getApprovalTable()
      .then(r => {
        const rs = r.data.rows || [];
        const alive = new Set(rs.map(x => x.order_item_id));

        // Фоновое обновление не должно рушить работу: выстроенный порядок,
        // отметки и вписанные поля остаются. Новые позиции дописываются в
        // конец, согласованные кем-то другим — исчезают.
        const byId    = new Map(rs.map(x => [x.order_item_id, x]));
        const kept    = rowsRef.current.filter(p => byId.has(p.order_item_id))
                                       .map(p => byId.get(p.order_item_id));
        const keptIds = new Set(kept.map(x => x.order_item_id));
        const fresh   = rs.filter(x => !keptIds.has(x.order_item_id));

        // При обычной загрузке восстанавливаем порядок из черновика:
        // строки, которых в нём нет (новые заказы), уходят вниз
        let plain = rs;
        const savedOrder = readApprovalDraft().order;
        if (!bg && savedOrder && savedOrder.length) {
          const pos = new Map(savedOrder.map((id, i) => [id, i]));
          plain = [...rs].sort((a, b) =>
            (pos.has(a.order_item_id) ? pos.get(a.order_item_id) : 1e9) -
            (pos.has(b.order_item_id) ? pos.get(b.order_item_id) : 1e9));
        }

        setRows(bg ? [...kept, ...fresh] : plain);
        // Отметки не сбрасываем даже на ручном 🔄 — это часть черновика
        setSel(prev => new Set([...prev].filter(id => alive.has(id))));
        setFields(prev => {
          const next = {};
          rs.forEach(x => {
            next[x.order_item_id] = prev[x.order_item_id] || {
              press_id: '',
              // Названное клиентом кладём сразу: строка уходит на согласование
              // и без открытия полей, а сервер всё равно возьмёт заказ.
              plate_size: x.dsp_maker || '',
              note:       x.pack_type || '',
            };
          });
          return next;
        });
        if (bg && fresh.length) toast.success(`Новых позиций: ${fresh.length}`);
      })
      .catch(() => { if (!bg) toast.error(t('Не удалось загрузить таблицу согласования')); })
      .finally(() => { inFlight.current = false; if (!bg) setLoading(false); });

    salesAPI.getMissingRecipes()
      .then(r => setNoRecipe(r.data || { count: 0, in_queue: 0, results: [] }))
      .catch(() => {});
  }, []);

  // Пишем черновик на каждое изменение: «случайно ушёл в другой раздел и
  // вернулся» больше ничего не стирает. Живёт до согласования или очистки.
  useEffect(() => {
    if (!rows.length) return;
    writeApprovalDraft({
      fields,
      sel: [...sel],
      order: rows.map(r => r.order_item_id),
    });
  }, [fields, sel, rows]);

  const resetDraft = async () => {
    const ok = await confirmToast(
      'Очистить черновик? Введённые пресс, размер плиты и примечания будут потеряны.',
      { danger: true, confirmText: 'Очистить' });
    if (!ok) return;
    clearApprovalDraft();
    setFields({});
    setSel(new Set());
    load();
    toast.success(t('Черновик очищен'));
  };

  // Заказ менеджера должен появляться сам: раз в 10 секунд и сразу при
  // возврате на вкладку — чтобы технолог не жал 🔄 и не ждал.
  // Пока вкладка скрыта, не опрашиваем — сервер не дёргаем зря.
  useEffect(() => {
    load();
    const refresh = () => { if (!document.hidden) load({ background: true }); };
    const timer = setInterval(refresh, 10000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  const setField = (id, key, val) =>
    setFields(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: val } }));

  const toggle = (id) => setSel(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Помеченный на удаление заказ согласовать нельзя — в «выделить всё» не берём
  const pickable = rows.filter(r => !r.marked);
  const allSelected = pickable.length > 0 && pickable.every(r => sel.has(r.order_item_id));
  const toggleAll = () =>
    setSel(allSelected ? new Set() : new Set(pickable.map(r => r.order_item_id)));

  // Пресс сразу всем выделенным — чаще всего смена идёт на один пресс
  const applyPressToSelected = (pressId) => {
    if (!pressId) return;
    setFields(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        if (sel.has(r.order_item_id)) {
          next[r.order_item_id] = { ...(next[r.order_item_id] || {}), press_id: pressId };
        }
      });
      return next;
    });
  };

  // Стрелки двигают выделенные строки по ВСЕЙ таблице (не внутри группы):
  // так технолог и собирает одинаковые матрицы вместе.
  const move = (dir) => {
    if (sel.size === 0) { toast(t('Отметьте строки, которые нужно сдвинуть')); return; }
    setRows(prev => {
      const arr = [...prev];
      const idxs = arr.map((r, i) => sel.has(r.order_item_id) ? i : -1).filter(i => i >= 0);
      if (idxs.length === 0) return prev;
      if (dir < 0) {
        if (idxs[0] === 0) return prev;
        idxs.forEach(i => { [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; });
      } else {
        if (idxs[idxs.length - 1] === arr.length - 1) return prev;
        [...idxs].reverse().forEach(i => { [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; });
      }
      return arr;
    });
  };

  const selectedRows = rows.filter(r => sel.has(r.order_item_id));
  const totalQty = selectedRows.reduce((s, r) => s + (r.quantity || 0), 0);

  const approve = async () => {
    if (selectedRows.length === 0) { toast.error(t('Не выбрано ни одной позиции')); return; }
    const noPress = selectedRows.filter(r => !(fields[r.order_item_id] || {}).press_id);
    if (noPress.length) {
      toast.error(`Не указан пресс: ${noPress.length} поз.`);
      return;
    }
    // Ночные называем вслух: их помечают заранее и на глаз, и ошибка здесь
    // всплывёт только ночью, когда спросить уже не у кого.
    const night = selectedRows.filter(r => shifts[r.order_item_id] === 'night').length;
    // День называем вслух так же, как ночную смену: не сегодняшнюю заявку
    // пресс не увидит до самого дня, и узнать об этом из молчания экрана
    // невозможно.
    const dayLine = planDate && planDate !== todayISO()
      ? `
На день: ${new Date(planDate).toLocaleDateString('ru-RU')} — до этого дня пресс её не увидит.`
      : '';
    if (!await confirmToast(
      (night
        ? `Согласовать ${selectedRows.length} поз. (${totalQty} шт)?
`
          + `Из них на НОЧЬ: ${night} поз. — уйдут отдельной заявкой.`
        : `Согласовать ${selectedRows.length} поз. (${totalQty} шт)?`) + dayLine,
      { confirmText: 'Согласовать' }
    )) return;

    setSaving(true);
    try {
      // Порядок массива = порядок в таблице = priority у оператора
      const items = selectedRows.map(r => ({
        order_item_id: r.order_item_id,
        press_id: Number(fields[r.order_item_id].press_id),
        plate_size: (fields[r.order_item_id].plate_size || '').trim(),
        note: (fields[r.order_item_id].note || '').trim(),
        shift: shifts[r.order_item_id] || 'day',
      }));
      const { data } = await productionAPI.approveBatch(
        planDate ? { items, plan_date: planDate } : { items });
      // Смен могло быть две — тогда и заявок две, и печатать надо обе
      const made = (data.requests || []).length > 1
        ? data.requests.map(x => `${x.number} (${x.shift === 'night' ? 'ночь' : 'день'}, ${x.count})`).join(', ')
        : data.number;
      toast.success(`✅ Заявка ${made}: ${data.count} поз., ${data.total_quantity} шт`);
      load();
      onApproved && onApproved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка согласования');
    } finally { setSaving(false); }
  };

  const TH = {
    padding: '7px 6px', fontSize: 11, fontWeight: 700, color: 'var(--muted)',
    textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  };
  const TD = { padding: '5px 6px', fontSize: 12, color: 'var(--text)', verticalAlign: 'middle' };

  if (loading) return <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>{t('Загрузка…')}</p>;

  // Позиции без рецепта — по id продукта, чтобы отметить строки таблицы
  const noRecipeIds = new Set((noRecipe.results || []).map(r => r.id));

  // Решение по складскому заказу. Оба endpoint-а уже есть — их же нажимает
  // экран «Разрешение на отгрузку». Второй раз ту же логику не заводим.
  const stockAct = async (o, kind) => {
    setStockBusy(o.id);
    try {
      if (kind === 'allow') {
        await salesAPI.allowShipment(o.id);
        toast.success(t('Разрешено — выпишите распоряжение складу'));
        loadStock();
        // Сразу открываем выписку по этому же заказу. Уводить человека на
        // другой экран, чтобы он там заново нашёл того же клиента и тот же
        // заказ, — потерянная минута и лишний шанс промахнуться строкой.
        setStockIssue(o);
      } else {
        await salesAPI.orderToProduction(o.id);
        toast.success(t('Заказ отправлен в согласование — он появился в таблице ниже'));
        loadStock();
        // Заказ стал обычным: его позиции должны сразу встать строками сюда,
        // иначе человек нажал кнопку и не увидел результата. Фоном — чтобы не
        // сбросить уже расставленный порядок и отмеченные строки.
        load({ background: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setStockBusy(null); }
  };

  return (
    <div>
      {/* Автоплан: кнопка, настройки и окно с предложенным планом */}
      {sessionStorage.getItem('autoplanner') === '1' && (
        <AutoPlanPanel onApplied={() => { load(); onApproved && onApproved(); }} />
      )}
      {/* Выписка распоряжения поверх экрана — сразу после «Разрешить».
          Разрешение без документа склад не видит: он грузит по распоряжению,
          и заказ, разрешённый отдельно, повисал между кабинетом и складом. */}
      {stockIssue && (
        <div onClick={() => setStockIssue(null)} style={{
          position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: 16, overflowY: 'auto',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg)', borderRadius: 14, padding: 16,
            maxWidth: 900, width: '100%', marginTop: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                          marginBottom: 12 }}>
              <b style={{ color: 'var(--text)', fontSize: 17, flex: 1 }}>
                {t('Распоряжение складу')} · {stockIssue.order_number}
              </b>
              <button onClick={() => setStockIssue(null)} style={{
                background: 'none', border: 'none', color: 'var(--dim)',
                fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <ShipmentNotes
              preset={{ clientId: stockIssue.client, orderId: stockIssue.id }}
              onIssued={() => { setStockIssue(null); loadStock(); }}
            />
          </div>
        </div>
      )}

      {/* ── Есть на складе: прессовать нечего ──────────────────────────────
          Полоса стоит НАД таблицей: пока решение не принято, заказ клиента
          не движется никуда, а таблица ниже — это работа на завтра. */}
      {stockOrders.length > 0 && (
        <div style={{
          background: '#0ea5e912', border: '2px solid #0ea5e955',
          borderRadius: 12, padding: '12px 16px', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                        flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              background: '#0ea5e9', color: '#fff', borderRadius: 20,
              padding: '2px 12px', fontSize: 18, fontWeight: 900,
            }}>{stockOrders.length}</span>
            <span style={{ color: '#0284c7', fontSize: 15, fontWeight: 800 }}>
              {t('заказов закрываются складом — прессовать нечего')}
            </span>
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 10 }}>
            {t('Товар уже лежит. Решите: придержать листы и сделать свежие — или разрешить отгрузку со склада.')}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stockOrders.map(o => (
              <div key={o.id} style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '10px 14px',
              }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                {/* Номер и заказчик раскрывают состав. Кнопки — рядом, но
                    отдельно: раскрыть заказ и разрешить отгрузку это разные
                    решения, и промахнуться мимо одного в другое нельзя. */}
                <button onClick={() => setStockOpen(v => v === o.id ? null : o.id)}
                  title={t('Показать, что в заказе')}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: 'inherit', display: 'flex', alignItems: 'center',
                    gap: 10, flex: 1, minWidth: 200, textAlign: 'left',
                  }}>
                  <span style={{ color: 'var(--dim)', fontSize: 12, width: 12 }}>
                    {stockOpen === o.id ? '▾' : '▸'}
                  </span>
                  <span style={{ color: 'var(--text)', fontWeight: 800, fontSize: 14 }}>
                    {o.order_number}
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: 13.5, flex: 1,
                                 minWidth: 0, overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.client_name}
                  </span>
                </button>
                <span style={{
                  background: '#22c55e18', color: '#16a34a', border: '1px solid #22c55e50',
                  borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}>📦 {t('есть на складе')}</span>
                <button onClick={() => stockAct(o, 'allow')} disabled={stockBusy === o.id}
                  className="touch-btn" style={{
                    background: '#0f9d58', border: 'none',
                    borderRadius: 9, color: '#fff', padding: '8px 16px',
                    fontSize: 13.5, fontWeight: 800, cursor: 'pointer',
                    fontFamily: 'inherit', whiteSpace: 'nowrap',
                  }}>{t('✅ Разрешить и выписать')}</button>
                <button onClick={() => stockAct(o, 'produce')} disabled={stockBusy === o.id}
                  className="touch-btn" style={{
                    background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 9, color: 'var(--muted)', padding: '8px 16px',
                    fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                    fontFamily: 'inherit', whiteSpace: 'nowrap',
                  }}>{t('🏭 Всё-таки прессовать')}</button>
              </div>
              {stockOpen === o.id && (
                <div style={{ marginTop: 10, paddingTop: 10,
                              borderTop: '1px solid var(--border)' }}>
                  <OrderBreakdown orderId={o.id} />
                </div>
              )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Рецепт заполняют не здесь и вспоминают о нём в последнюю очередь.
          Поэтому предупреждение стоит на самом видном месте и мигает: пока
          рецепта нет, выпуск уходит в 1С без материалов и без себестоимости,
          а замечают это через месяц, когда сводить уже поздно. */}
      {noRecipe.count > 0 && (
        <div
          onClick={() => setShowNoRecipe(v => !v)}
          className="blink-warn"
          style={{
            background: '#ef444418', border: '2px solid #ef4444',
            borderRadius: 12, padding: '12px 16px', marginBottom: 12,
            cursor: 'pointer',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{
              background: '#ef4444', color: '#fff', borderRadius: 20,
              padding: '2px 12px', fontSize: 18, fontWeight: 900,
            }}>{noRecipe.count}</span>
            <span style={{ color: '#ef4444', fontSize: 15, fontWeight: 800 }}>
              {t('позиций производятся БЕЗ РЕЦЕПТА')}
            </span>
            {noRecipe.in_queue > 0 && (
              <span style={{ color: '#f59e0b', fontSize: 13, fontWeight: 700 }}>
                · {t('из них уже на прессе')}: {noRecipe.in_queue}
              </span>
            )}
            <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>
              {showNoRecipe ? t('свернуть') : t('показать список')}
            </span>
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 6 }}>
            {t('Без рецепта в 1С не спишется сырьё и не посчитается себестоимость. Производство при этом идёт как обычно.')}
          </div>

          {showNoRecipe && (
            <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto' }}>
              {noRecipe.results.map(r => (
                <div key={r.id} style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  padding: '4px 0', borderTop: '1px solid #ef444430', fontSize: 13,
                }}>
                  <span style={{ flex: 1, color: 'var(--text)' }}>{r.name}</span>
                  {r.in_queue && <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 700 }}>{t('НА ПРЕССЕ')}</span>}
                  {!r.in_queue && r.pending && <span style={{ color: '#f59e0b', fontSize: 11 }}>{t('в заказах')}</span>}
                  {!r.in_queue && !r.pending && <span style={{ color: 'var(--dim)', fontSize: 11 }}>{t('выпускали')}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Сделано не в свою смену. Технолог помечает работу заранее, а цех
          живёт своим порядком: ночную позицию начинают в обед, дневную
          дожимают под утро. Само по себе это не ошибка — но отчёт за смену
          тогда врёт, а узнают об этом при разборе месяца. */}
      {mismatch.length > 0 && (
        <div style={{
          background: '#6366f114', border: '1px solid #6366f155',
          borderRadius: 12, padding: '14px 18px', marginBottom: 14,
        }}>
          <div style={{ color: '#818cf8', fontWeight: 700, marginBottom: 4 }}>
            🌗 {t('Сделано не в свою смену')} — {mismatch.length}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 10 }}>
            {t('Перенести — позиция уйдёт в ту смену, которая её действительно делала. Оставить — останется так, как вы записали.')}
          </div>
          {mismatch.map(r => (
            <div key={r.queue_item_id} style={{
              display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
              padding: '7px 0', borderTop: '1px solid var(--border)', fontSize: 13,
            }}>
              <span style={{ flex: '1 1 260px', minWidth: 0, color: 'var(--text)' }}>
                <b>{r.product}</b>
                <span style={{ color: 'var(--dim)' }}> · {r.client}</span>
                {r.order_number && <span style={{ color: '#f59e0b' }}> · {r.order_number}</span>}
                <span style={{ display: 'block', color: 'var(--dim)', fontSize: 11.5, marginTop: 2 }}>
                  {r.press} · {r.quantity_done} шт ·{' '}
                  {new Date(r.started_at).toLocaleString('ru-RU',
                    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                {t('помечено')} <b style={{ color: 'var(--text)' }}>{r.planned_display}</b>
                {' → '}
                {t('сделано')} <b style={{ color: '#818cf8' }}>{r.fact_display}</b>
              </span>
              <button onClick={() => resolveShift(r, 'move')} style={{
                background: '#6366f1', border: 'none', borderRadius: 8, color: '#fff',
                padding: '7px 14px', fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>{t('Перенести в')} {r.fact_display}</button>
              <button onClick={() => resolveShift(r, 'keep')} style={{
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
                color: 'var(--muted)', padding: '7px 14px', fontSize: 12.5,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>{t('Оставить')}</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={load} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--dim)', padding: '6px 12px', cursor: 'pointer' }}>🔄</button>
        <span style={{ color: 'var(--dim)', fontSize: 12 }}>
          {t('Позиций:')} <b style={{ color: 'var(--text)' }}>{shownRows.length}</b>
          {shownRows.length !== rows.length && (
            <span style={{ color: 'var(--dim)' }}> {t('из')} {rows.length}</span>
          )}
        </span>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Заказчик, продукт, матрица, заказ…')}
          style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text)', padding: '6px 12px',
            fontSize: 13, minWidth: 240, outline: 'none', fontFamily: 'inherit',
          }} />
        {/* На какой день. Стоит перед сменой: сначала день, потом смена внутри
            дня — так же, как это называют вслух. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 12.5, color: 'var(--muted)' }}>
          {t('На день')}
          <input type="date" value={planDate} min={todayISO()}
            onChange={e => setPlanDate(e.target.value)}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', padding: '5px 8px',
              fontSize: 13, fontFamily: 'inherit', outline: 'none',
            }} />
          {planDate && (
            <button onClick={() => setPlanDate('')} title={t('Сегодня')}
              style={{
                background: 'none', border: 'none', color: 'var(--dim)',
                cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 2,
              }}>✕</button>
          )}
        </label>
        <select
          onChange={e => {
            const v = e.target.value;
            e.target.value = '';
            if (!v) return;
            setShifts(p => {
              const n = { ...p };
              rows.forEach(r => { if (sel.has(r.order_item_id)) n[r.order_item_id] = v; });
              return n;
            });
          }}
          defaultValue=""
          style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text)', padding: '6px 10px',
            fontSize: 13, fontFamily: 'inherit',
          }}>
          <option value="">{t('Смена — всем отмеченным')}</option>
          <option value="day">☀️ {t('День')}</option>
          <option value="night">🌙 {t('Ночь')}</option>
        </select>
        <select
          onChange={e => { applyPressToSelected(e.target.value); e.target.value = ''; }}
          defaultValue=""
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)', padding: '6px 10px', fontSize: 12 }}
        >
          <option value="">{t('Пресс — всем отмеченным')}</option>
          {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {canCloseSel && (
          <button onClick={closeSelected} disabled={sel.size === 0 || closingSel}
            title={t('Закрыть заказы отмеченных строк без уведомления клиенту')}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
              color: '#ef4444', padding: '6px 12px', fontSize: 12, fontFamily: 'inherit',
              cursor: sel.size === 0 || closingSel ? 'default' : 'pointer',
              opacity: sel.size === 0 || closingSel ? 0.5 : 1,
            }}>
            {t('Закрыть заказы отмеченных строк')} ({selOrders.size} {t('заказов')})
          </button>
        )}
        {/* Черновик живёт в браузере до согласования — можно уйти и вернуться */}
        <span style={{ color: 'var(--dim)', fontSize: 11, marginLeft: 'auto' }}>
          💾 {t('черновик сохраняется автоматически')}
        </span>
        <button onClick={resetDraft}
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: '#ef4444', padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>
          {t('Очистить черновик')}
        </button>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>
          {t('Нет позиций, ожидающих согласования')}
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, width: 34 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th style={{ ...TH, width: 92 }}>{t('Смена')}</th>
                  <th style={{ ...TH, width: 110 }}>{t('№ пресса')}</th>
                  <th style={{ ...TH, width: 70 }}>{t('Толщина мм')}</th>
                  {/* Почему позиция стоит именно здесь. Порядок в смене не
                      произвольный: сначала тёмное, потом светлое — грязь на
                      прессе не видна на тёмном, а к светлым его успевают
                      протереть; внутри цвета от холодного режима к горячему —
                      нагреть быстро, остыть плита сама не спешит. Строки уже
                      пришли в этом порядке, стрелками его можно переставить. */}
                  <th style={{ ...TH, width: 86 }}
                      title="Тон цвета и эталонная температура — по ним предложен порядок">
                    {t('Цвет · °C')}
                  </th>
                  <th style={{ ...TH, width: 56 }} title={t('Когда заказ приняли')}>{t('Принят')}</th>
                  <th style={TH}>{t('Заказчик')}</th>
                  <th style={TH}>{t('Наименование')}</th>
                  <th style={TH}>{t('Матрица')}</th>
                  <th style={{ ...TH, width: 80 }}>{t('Кол-во план')}</th>
                  {/* «Размер плиты» и «Примечание» — так эти графы назывались
                      с самого начала, но пишут в них другое: производителя
                      плиты («чфмк», «Новус») и упаковку («Упаковка 2х»).
                      Заголовок расходился с содержимым, и заполнял их
                      технолог по памяти о разговоре, при котором его не было.
                      Теперь их называет менеджер при заказе. */}
                  <th style={{ ...TH, width: 130 }}>{t('Производитель ДСП')}</th>
                  <th style={{ ...TH, width: 130 }}>{t('Тип упаковки')}</th>
                  <th style={{ ...TH, width: 34 }}></th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r, i) => {
                  const fv = fields[r.order_item_id] || {};
                  const checked = sel.has(r.order_item_id);
                  // Матрица сменилась — показываем разделитель, как у оператора
                  const prev = i > 0 ? shownRows[i - 1] : null;
                  // Полоса стоит над КАЖДОЙ матрицей, включая первую: раньше
                  // первая группа шла без заголовка, и её объём было негде
                  // написать — а вопрос «сколько тут работы» у неё тот же.
                  const showDiv = i === 0 || prev.texture_name !== r.texture_name;
                  const tot = texTotals.get(r.texture_name || '—')
                              || { qty: 0, rows: 0, marked: 0 };
                  return (
                    <React.Fragment key={r.order_item_id}>
                      {showDiv && (
                        <tr>
                          <td colSpan={13} style={{
                            padding: '5px 8px', fontSize: 12, fontWeight: 700,
                            color: '#f59e0b', background: '#f59e0b12',
                            borderTop: '1px solid #f59e0b40', borderBottom: '1px solid #f59e0b40',
                          }}>
                            ⚙ {i === 0 ? 'матрица' : 'замена матрицы'} &raquo; {r.texture_name}
                            <span style={{ marginLeft: 10, color: 'var(--text)' }}>
                              {tot.qty.toLocaleString('ru-RU')} шт · строк: {tot.rows}
                            </span>
                            {tot.marked > 0 && (
                              <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--dim)' }}>
                                (помеченные на удаление не в счёт: {tot.marked})
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                      <tr style={{
                        background: checked ? 'var(--accent-weak)' : 'transparent',
                        // Помечен на удаление — серый, как в 1С, и не выбирается
                        opacity: r.marked ? 0.5 : 1,
                        textDecoration: r.marked ? 'line-through' : 'none',
                      }}>
                        <td style={TD}>
                          <input type="checkbox" checked={checked} disabled={r.marked}
                            title={r.marked ? t('Заказ помечен на удаление') : undefined}
                            onChange={() => toggle(r.order_item_id)} />
                        </td>
                        {/* Смена. Ночная работа помечается заранее: цех
                            должен видеть свою очередь, а отчёт — делиться по
                            сменам. Ночь выделяем цветом: в списке из тридцати
                            строк её ищут глазами. */}
                        <td style={TD}>
                          <select
                            value={shifts[r.order_item_id] || 'day'}
                            onChange={e => setShifts(p => ({
                              ...p, [r.order_item_id]: e.target.value }))}
                            style={{
                              width: '100%', padding: '4px 6px', fontSize: 12,
                              background: (shifts[r.order_item_id] === 'night')
                                ? '#6366f126' : 'var(--bg)',
                              border: '1px solid ' + ((shifts[r.order_item_id] === 'night')
                                ? '#6366f1' : 'var(--border)'),
                              borderRadius: 5, color: 'var(--text)',
                              fontWeight: (shifts[r.order_item_id] === 'night') ? 700 : 400,
                            }}>
                            <option value="day">☀️ День</option>
                            <option value="night">🌙 Ночь</option>
                          </select>
                        </td>
                        <td style={TD}>
                          <select
                            value={fv.press_id || ''}
                            onChange={e => setField(r.order_item_id, 'press_id', e.target.value)}
                            style={{
                              width: '100%', padding: '4px 6px', fontSize: 12,
                              background: 'var(--bg)', border: '1px solid ' + (checked && !fv.press_id ? '#ef4444' : 'var(--border)'),
                              borderRadius: 5, color: 'var(--text)',
                            }}
                          >
                            <option value="">—</option>
                            {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td style={TD}>{r.thickness}</td>
                        <td style={TD}>
                          {(() => {
                            const q = r.seq || {};
                            // Тон рисуем полоской: цифру «22» глазом не
                            // сравнить, а тёмное от светлого видно сразу
                            const tone = Math.max(0, Math.min(100, q.tone ?? 50));
                            const grey = Math.round(255 * tone / 100);
                            return (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span title={q.tone_exact
                                  ? `Тон ${tone} — из карточки продукта`
                                  : `Тон ${tone} — угадан по названию, поставьте в карточке`}
                                  style={{
                                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                                    background: `rgb(${grey},${grey},${grey})`,
                                    border: q.tone_exact
                                      ? '1px solid var(--border)'
                                      : '1px dashed #f59e0b',
                                  }} />
                                <span style={{ color: q.temp ? 'var(--text)' : 'var(--dim)' }}>
                                  {q.temp ? `${q.temp}°` : '—'}
                                </span>
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                          {fmtOrderDate(r.order_date)}
                        </td>
                        <td style={TD}>
                          {r.client_name}
                          {r.is_service && <> <ServiceTag /></>}
                        </td>
                        <td style={TD}>
                          {r.product_name}
                          {noRecipeIds.has(r.product_id) && (
                            <span title={t('Нет рецепта — сырьё в 1С не спишется')}
                              style={{
                                marginLeft: 6, background: '#ef4444', color: '#fff',
                                borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700,
                              }}>{t('БЕЗ РЕЦЕПТА')}</span>
                          )}
                          {/* Заказ приняли, когда сырья не хватало. Поставить
                              такую позицию на пресс можно, но знать об этом
                              надо ДО того, как пресс встанет без плиты. */}
                          {r.stock_warning && (
                            <span title={r.stock_warning}
                              style={{
                                marginLeft: 6, background: '#f59e0b', color: '#fff',
                                borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700,
                              }}>{t('НЕ ХВАТАЛО СЫРЬЯ')}</span>
                          )}
                        </td>
                        <td style={TD}>{r.texture_name}</td>
                        <td style={{ ...TD, fontWeight: 700 }}>{r.quantity}</td>
                        {/* Названо клиентом — не правим: это условие заказа,
                            а не решение цеха. Не названо — набирает технолог,
                            как раньше, с подсказками из истории. */}
                        <td style={TD}>
                          {r.dsp_maker ? (
                            <span title={t('Из заказа — изменить нельзя')}
                              style={{ color: 'var(--text)', fontWeight: 600 }}>
                              {r.dsp_maker}
                              <span style={{ color: 'var(--dim)', fontSize: 10, marginLeft: 5 }}>
                                🔒
                              </span>
                            </span>
                          ) : (
                            <SuggestInput
                              kind="plate_size"
                              value={fv.plate_size || ''}
                              onChange={v => setField(r.order_item_id, 'plate_size', v)}
                              placeholder={t('производитель')}
                            />
                          )}
                        </td>
                        <td style={TD}>
                          {r.pack_type ? (
                            <span title={t('Из заказа — изменить нельзя')}
                              style={{ color: 'var(--text)', fontWeight: 600 }}>
                              {r.pack_type}
                              <span style={{ color: 'var(--dim)', fontSize: 10, marginLeft: 5 }}>
                                🔒
                              </span>
                            </span>
                          ) : (
                            <SuggestInput
                              kind="note"
                              value={fv.note || ''}
                              onChange={v => setField(r.order_item_id, 'note', v)}
                              placeholder={t('упаковка')}
                            />
                          )}
                        </td>
                        {/* Записали не тот товар — строку надо убрать здесь, а
                            не согласовывать заведомо лишнее, чтобы потом
                            выковыривать её из очереди. */}
                        <td style={TD}>
                          {isAdmin && <button onClick={() => removeRow(r)}
                            title={t('Убрать позицию из заказа')}
                            style={{
                              background: 'none', border: '1px solid var(--border)',
                              borderRadius: 7, color: '#ef4444', padding: '4px 9px',
                              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                            }}>✕</button>}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Стрелки — как в шаблоне, отдельной колонкой справа */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 30 }}>
            <button onClick={() => move(-1)} title={t('Поднять отмеченные')}
              style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--muted)', padding: '8px 12px', cursor: 'pointer', fontSize: 15 }}>⌃</button>
            <button onClick={() => move(1)} title={t('Опустить отмеченные')}
              style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--muted)', padding: '8px 12px', cursor: 'pointer', fontSize: 15 }}>⌄</button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, marginTop: 12,
          padding: '10px 14px', background: 'var(--bg)', borderRadius: 10, flexWrap: 'wrap',
        }}>
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>
            {t('Выбрано:')} <b style={{ color: 'var(--text)' }}>{selectedRows.length}</b> {t('поз.')}
          </span>
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>
            {t('Итого:')} <b style={{ color: '#22c55e', fontSize: 16 }}>{totalQty}</b> {t('шт')}
          </span>
          <Btn variant="success" size="md" onClick={approve}
            disabled={saving || selectedRows.length === 0}
            style={{ marginLeft: 'auto' }}>
            {saving ? 'Согласование…' : '✅ Согласовать'}
          </Btn>
        </div>
      )}
    </div>
  );
}


// ── Заводы-изготовители плиты и плёнки ──────────────────────────────────────
//
// Оператор у пресса выбирает завод из этого списка и не пишет его руками:
// «Кроношпан», «кроношпан» и «KRONOSPAN» — один завод, и в разборе брака они
// обязаны сойтись в одну строку. Номер партии и паллеты списком не бывают
// никогда — на накладной он каждый раз новый, их оператор набирает как есть.
//
// Не удаляем, а снимаем «активен»: за заводом стоят смены прошлых месяцев, и
// они должны и дальше называть, чья это была плита.
function MaterialMakers() {
  // Свой стиль поля: тот же INP объявлен внутри соседних экранов, и снаружи
  // его не видно. В дев-сборке это было предупреждением, а продакшн-сборка на
  // таком падает — и падает уже при выкладке.
  const INP = {
    width: '100%', padding: '9px 12px', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, boxSizing: 'border-box',
  };
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName]   = useState('');
  const [kind, setKind]   = useState('both');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    warehouseAPI.getMaterialMakers({ all: 1 })
      .then(r => setRows(r.data.results || r.data || []))
      .catch(() => toast.error(t('Справочник не загрузился')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error(t('Назовите завод')); return; }
    setSaving(true);
    try {
      await warehouseAPI.createMaterialMaker({ name: name.trim(), kind });
      setName('');
      toast.success(t('Завод добавлен'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.name?.[0]
        || e.response?.data?.error || 'Не удалось добавить');
    } finally { setSaving(false); }
  };

  const patch = async (row, data) => {
    try {
      await warehouseAPI.updateMaterialMaker(row.id, data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Не удалось сохранить');
    }
  };

  const KINDS = [
    { v: 'dsp',  label: 'Плита (ДСП)' },
    { v: 'film', label: 'Плёнка' },
    { v: 'both', label: 'И плита, и плёнка' },
  ];

  // Заводы уже названы в справочнике 1С — в артикуле («Интерпринт»,
  // «Кроношпан Б (молет)»), иногда в поле «Производитель». Набирать тот же
  // список во второй раз руками незачем. Но заводим не молча: список заводов
  // учётный, и строка по догадке в нём — это чужая цифра в отчёте.
  const [found, setFound] = useState(null);      // null — ещё не искали
  const [picked, setPicked] = useState({});      // name -> kind
  const [scanning, setScanning] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await warehouseAPI.makerCandidates();
      const list = r.data.results || [];
      setFound(list);
      // Отмечаем те, что встречаются не один раз: одиночное написание чаще
      // всего опечатка в карточке, а не отдельный завод.
      const pre = {};
      list.forEach(x => { if (x.count > 1) pre[x.name] = x.kind; });
      setPicked(pre);
      if (list.length === 0) toast(t('В справочнике 1С новых заводов не нашлось'));
    } catch (e) {
      toast.error(t('Не удалось прочитать справочник'));
    } finally { setScanning(false); }
  };

  const addPicked = async () => {
    const items = Object.entries(picked).map(([name, kind]) => ({ name, kind }));
    if (items.length === 0) { toast.error(t('Отметьте, что добавить')); return; }
    try {
      const r = await warehouseAPI.collectMakers(items);
      toast.success(`Добавлено заводов: ${r.data.created}`);
      setFound(null); setPicked({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Не удалось добавить');
    }
  };

  return (
    <div style={{ background: 'var(--card)', borderRadius: 12, padding: 16 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12, maxWidth: 720 }}>
        Список, из которого оператор выбирает завод плиты и плёнки при старте
        смены. Обязательным его делает администратор — по цеху отдельно
        («Требовать завод плиты и плёнки»).
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input value={name} onChange={e => setName(e.target.value)}
          placeholder={t('Название завода')} style={{ ...INP, flex: 1, minWidth: 220 }}
          onKeyDown={e => { if (e.key === 'Enter') add(); }} />
        <select value={kind} onChange={e => setKind(e.target.value)}
          style={{ ...INP, width: 210, cursor: 'pointer' }}>
          {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
        <Btn size="md" onClick={add} disabled={saving}>
          {saving ? '…' : '+ Добавить'}
        </Btn>
        <Btn variant="ghost" size="md" onClick={scan} disabled={scanning}>
          {scanning ? 'Смотрю…' : '🔍 Взять из справочника 1С'}
        </Btn>
      </div>

      {found && found.length > 0 && (
        <div style={{
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 14, marginBottom: 14,
        }}>
          <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Найдено в справочнике 1С: {found.length}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 12.5, marginBottom: 10, maxWidth: 720 }}>
            Взято из артикула и поля «Производитель» у плит и плёнки. Номера
            декора и оттенки отрезаны: «Интерпринт 091006/751» — это
            «Интерпринт». Отмечено то, что встречается не один раз; проверьте
            и снимите лишнее.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4,
                        maxHeight: 320, overflowY: 'auto' }}>
            {found.map(x => {
              const on = Object.prototype.hasOwnProperty.call(picked, x.name);
              return (
                <div key={x.name} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 8px', borderRadius: 8,
                  background: on ? 'var(--accent-weak)' : 'transparent',
                }}>
                  <input type="checkbox" checked={on} style={{ width: 17, height: 17 }}
                    onChange={e => setPicked(prev => {
                      const next = { ...prev };
                      if (e.target.checked) next[x.name] = x.kind;
                      else delete next[x.name];
                      return next;
                    })} />
                  <span style={{ flex: 1, color: 'var(--text)', fontSize: 14 }}>{x.name}</span>
                  <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                    в {x.count} позициях
                  </span>
                  <select value={on ? picked[x.name] : x.kind} disabled={!on}
                    onChange={e => setPicked(prev => ({ ...prev, [x.name]: e.target.value }))}
                    style={{ ...INP, width: 190, cursor: on ? 'pointer' : 'default' }}>
                    {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <Btn size="md" onClick={addPicked}>
              ✅ Добавить отмеченные ({Object.keys(picked).length})
            </Btn>
            <Btn variant="ghost" size="md" onClick={() => { setFound(null); setPicked({}); }}>
              {t('Закрыть')}
            </Btn>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20, fontSize: 13 }}>{t('Загрузка...')}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20, fontSize: 14 }}>
          Пока пусто. Заведите заводы, у которых берёте плиту и плёнку.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(row => (
            <div key={row.id} style={{
              background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
              opacity: row.is_active ? 1 : 0.5,
            }}>
              <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, flex: 1 }}>
                {row.name}
              </span>
              <select value={row.kind}
                onChange={e => patch(row, { kind: e.target.value })}
                style={{ ...INP, width: 200, cursor: 'pointer' }}>
                {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
              <Btn variant="ghost" size="sm"
                onClick={() => patch(row, { is_active: !row.is_active })}>
                {row.is_active ? 'Снять с учёта' : 'Вернуть'}
              </Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TechnologPage() {
  const [presses, setPresses] = useState([]);
  const [queue, setQueue] = useState([]);
  const [textures, setTextures] = useState([]);
  const [statusFilter, setStatusFilter] = useState('waiting');
  const [loading, setLoading] = useState(true);
  const [pressFilter, setPressFilter] = useState('');
  // Поиск по очереди: в ней под три десятка позиций, и ищут в ней конкретный
  // заказ или клиента, а не листают подряд
  const [qSearch, setQSearch] = useState('');

  // Разделы страницы — в боковом меню, а не гармошкой в содержимом. Раскрытые
  // блоки шли один под другим, и до «Настройки сортов» надо было прокрутить
  // мимо очереди на полсотни строк; свёрнутые — превращали экран в семь
  // одинаковых полос, по которым не видно, где ты. Слева видно и то, и другое:
  // где ты сейчас и что вообще есть.
  // Без заголовков групп: слово «Работа» уже стоит над самим меню страниц, и
  // второе такое же строкой ниже читалось как переход в другое место. Порядок
  // и так говорит всё нужное: сверху ежедневное, ниже справочники.
  // Разделов было тринадцать, и все одним шрифтом: «Согласование заказов»,
  // которое открывают по десять раз на дню, стояло рядом с «Короткими
  // именами», куда заходят раз в месяц. Глазу не за что зацепиться — и
  // технолог каждый раз перечитывал весь список.
  //
  // Теперь на главном три дела с числом ждущего, а справочники — за одной
  // дверью. Каждодневное осталось и в меню: до него один щелчок, не два.
  // Отгрузку выписывает не всякий технолог, а тот, кому это разрешили
  // галочкой. Раздел показываем только ему — остальным он ни о чём.
  const canShip = ['director', 'admin'].includes(sessionStorage.getItem('role'))
    || sessionStorage.getItem('can_ship') === '1';

  const GROUPS = [
    { title: '', items: [
      { key: 'home',          label: '🏠 Главное' },
    ]},
    { title: 'Каждый день', items: [
      { key: 'approval',      label: '✅ Что запустить в работу' },
      { key: 'stockOrders',   label: '📦 Разрешение на отгрузку' },
      ...(canShip ? [{ key: 'shipnotes', label: '🚚 Отгрузка' }] : []),
      { key: 'queue',         label: '📋 Очередь производства' },
    ]},
    // Режим вынесен из справочников в меню отдельной строкой.
    //
    // Он лежал в карточке ЛДСП и в текстурах — двух разных разделах, — а
    // плитка «Характеристики прессов» обещала «эталонный режим: температура,
    // время, давление» и не содержала ни одного из этих полей. Найти его не
    // мог даже тот, кто это писал.
    { title: 'Технология', items: [
      { key: 'etalon',        label: '🌡 Эталонные режимы' },
    ]},
    { title: 'Настройка', items: [
      { key: 'refs',          label: '⚙ Справочники' },
    ]},
  ];

  // Что лежит за дверью «Справочники». В меню их нет — они и не каждодневные.
  const REF_TILES = [
    { key: 'recipes',       icon: '🧾', title: 'Рецепты', small: true, color: 'var(--accent)',
      hint: 'Из чего делается лист: плёнка, ДСП, клей' },
    { key: 'substitutes',   icon: '🔁', title: 'Замены', small: true, color: '#8b5cf6',
      hint: 'Одинаковые плиты под разными именами — одной группой' },
    { key: 'presses',       icon: '⚙️', title: 'Состояние прессов', small: true, color: '#0ea5e9',
      hint: 'Что стоит на прессе и какая норма' },
    // Раньше здесь стояло «Эталонный режим: температура, время, давление».
    // Внутри этого нет и не было: название, норма выработки, активность.
    // Именно на эту подпись и шли за режимом — и не находили.
    { key: 'pressSettings', icon: '🛠', title: 'Характеристики прессов', small: true, color: '#0ea5e9',
      hint: 'Название пресса и норма выработки (листов в час)' },
    { key: 'textures',      icon: '🎨', title: 'Текстуры пресса', small: true, color: '#f59e0b',
      hint: 'Матрицы: гладкий, молет, мягкое дерево' },
    { key: 'grades',        icon: '🏷', title: 'Сорта качества', small: true, color: '#f59e0b',
      hint: 'Список сортов и брака, из которого выбирают у пресса' },
    // Была тремя строками — «ЛДСП», «ДСП», «Бумага и плёнка», — и все три
    // открывали один экран. Категория теперь переключается внутри него.
    { key: 'nom_ldsp',      icon: '📦', title: 'Номенклатура', small: true, color: '#64748b',
      hint: 'ЛДСП, плиты, плёнка и бумага — одним списком' },
    { key: 'shortnames',    icon: '✏', title: 'Короткие имена', small: true, color: '#64748b',
      hint: 'Как позиция называется на экранах цеха' },
    { key: 'makers',        icon: '🏭', title: 'Заводы-изготовители', small: true, color: '#64748b',
      hint: 'Чья плита и чья плёнка — из этого списка выбирает пресс' },
  ];
  const [view, setView] = useState('home');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, q, t] = await Promise.all([
        productionAPI.getPresses(),
        // planned=1 — вместе с тем, что выписано на будущие дни. У пресса
        // такой работы ещё нет, но технолог её и планировал: без неё
        // согласованная позиция просто исчезала бы с обоих экранов.
        productionAPI.getQueue(pressFilter
          ? { press_id: pressFilter, planned: 1 } : { planned: 1 }),
        warehouseAPI.getTextures(),
      ]);
      setPresses(p.data);
      setQueue(q.data);
      setTextures(t.data);
    } catch {
      toast.error(t("Ошибка загрузки данных"));
    } finally {
      setLoading(false);
    }
  }, [pressFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredQueue = queue.filter(item => {
    if (statusFilter && item.status !== statusFilter) return false;
    // Любой кусок, в любом порядке: в очереди под три десятка позиций, и
    // помнят из них цвет да хвост номера заказа.
    return makeMatcher(qSearch)(item.order_number, item.client_name,
                                item.product_name, item.texture_name);
  });

  const statCount = (s) => queue.filter(i => i.status === s).length;

  // Числа на плитках: видно, есть ли работа, не открывая раздел
  const [pendingCnt, setPendingCnt] = useState(0);
  const [stockCnt, setStockCnt]     = useState(0);
  useEffect(() => {
    productionAPI.getApprovalTable()
      .then(r => setPendingCnt((r.data.rows || []).length))
      .catch(() => {});
    salesAPI.getOrders({ status: 'stock' })
      .then(r => setStockCnt((r.data.results || r.data || []).length))
      .catch(() => {});
  }, [view]);

  const HOME_TILES = [
    { key: 'approval', icon: '✅', title: 'Что запустить в работу',
      hint: 'Согласовать заказы и раздать их прессам',
      count: pendingCnt, color: 'var(--accent)' },
    { key: 'stockOrders', icon: '📦', title: 'Разрешение на отгрузку',
      hint: 'Заказ закрывается складом — прессовать нечего, нужно только разрешить',
      count: stockCnt, color: '#0ea5e9' },
    ...(canShip ? [{ key: 'shipnotes', icon: '🚚', title: 'Отгрузка',
      hint: 'Выписать складу распоряжение — что и сколько выдать клиенту',
      count: 0, color: '#22c55e' }] : []),
    { key: 'queue', icon: '📋', title: 'Очередь производства',
      hint: 'Что стоит на прессах прямо сейчас',
      count: statCount('waiting'), color: '#f59e0b' },
    { key: 'etalon', icon: '🌡', title: 'Эталонные режимы',
      hint: 'Как прессовать: температура, время, давление — одним списком',
      count: 0, color: '#8b5cf6' },
  ];

  return (
    <Layout title={t("Технолог — Управление очередью")}
            sections={GROUPS} section={view} onSection={setView}
            /* Справочники в меню не стоят — они плитками за дверью
               «Справочники». Снаружи, из поиска, это такие же разделы: без
               этого списка ссылка на «Короткие имена» молча приводила бы на
               главный экран. */
            alsoSections={['recipes', 'substitutes', 'presses', 'pressSettings',
                           'textures', 'grades', 'nom_ldsp', 'shortnames',
                           'makers']}>

      {/* Уйти отсюда можно всегда одинаково — иначе выход ищут заново */}
      {view !== 'home' && view !== 'refs' && (
        <BackHome onBack={() => setView('home')} />
      )}

      {/* Главное: три дела, ради которых технолог сюда заходит */}
      {view === 'home' && (<>
        <TilesHead title="Что делаем сегодня"
          hint="Числа рядом — сколько ждёт. Пусто значит сделано." />
        <TileGrid tiles={HOME_TILES} onPick={setView} />
        <div style={{ marginTop: 22 }}>
          <TilesHead title="Справочники"
            hint="Открывают редко: рецепты, замены, настройки прессов и списки" />
          <TileGrid tiles={REF_TILES} onPick={setView} min={230} />
        </div>
      </>)}

      {/* Дверь «Справочники» из меню — тот же список, без дел сверху */}
      {view === 'refs' && (<>
        <BackHome onBack={() => setView('home')} />
        <TilesHead title="Справочники"
          hint="Всё, что настраивают редко. Каждодневное — на главном экране." />
        <TileGrid tiles={REF_TILES} onPick={setView} min={230} />
      </>)}

      {/* Прессы */}
      {view === 'approval' && (<>
        <ApprovalTable presses={presses} onApproved={loadAll} />
      </>)}

      {view === 'stockOrders' && <StockOrders />}

      {/* Раньше распоряжение выписывалось только из карточки заказа: машина
          пришла без заказа — и выписать было нечем, приходилось звать
          менеджера. Здесь тот же экран целиком: и выписка, и список
          выписанного. */}
      {view === 'shipnotes' && <ShipmentNotes />}

      {view === 'presses' && (<>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {presses.map(press => (
            <PressCard
              key={press.id}
              press={press}
              textures={textures}
              onUpdate={loadAll}
            />
          ))}
          {presses.length === 0 && !loading && (
            <div style={{ color: 'var(--mid)', fontSize: 14, gridColumn: '1/-1' }}>
              Прессы не найдены. Добавьте через Admin-панель.
            </div>
          )}
        </div>
      </>)}

      {/* Очередь */}
      {view === 'queue' && (<>
        {/* Шапка очереди */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Фильтр по статусу */}
            {[
              { key: '', label: `Все (${queue.length})` },
              { key: 'waiting', label: `Ожидают (${statCount('waiting')})` },
              { key: 'in_progress', label: `В работе (${statCount('in_progress')})` },
              { key: 'done', label: `Готово (${statCount('done')})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{
                  background: statusFilter === key ? 'var(--accent)' : 'var(--bg)',
                  border: '1px solid ' + (statusFilter === key ? 'var(--accent)' : 'var(--border)'),
                  borderRadius: 8, color: statusFilter === key ? '#fff' : 'var(--muted)',
                  padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 500
                }}
              >
                {label}
              </button>
            ))}

            {/* Фильтр по прессу */}
            <select
              value={pressFilter}
              onChange={e => setPressFilter(e.target.value)}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--muted)', padding: '6px 12px', fontSize: 13
              }}
            >
              <option value="">{t('Все прессы')}</option>
              {presses.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            {/* Поиск: в очереди под три десятка позиций, и глазами в ней ищут
                конкретный заказ или клиента, а не листают подряд */}
            <input value={qSearch} onChange={e => setQSearch(e.target.value)}
              placeholder={t('Заказ, клиент, продукт…')}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text)', padding: '6px 12px',
                fontSize: 13, minWidth: 200, outline: 'none', fontFamily: 'inherit',
              }} />

            <Btn variant="ghost" onClick={loadAll}>{t('🔄 Обновить')}</Btn>
          </div>
        </div>

        {/* Список */}
        {loading ? (
          <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 30 }}>{t('Загрузка...')}</p>
        ) : filteredQueue.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <p style={{ color: 'var(--mid)', fontSize: 15 }}>
              {statusFilter === 'waiting' ? 'Нет ожидающих заданий' : 'Список пуст'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredQueue.map(item => (
              <QueueRow
                key={item.id}
                item={item}
                presses={presses}
                onUpdate={loadAll}
              />
            ))}
          </div>
        )}
      </>)}

      {/* Номенклатура и рецепты */}
      {view.startsWith('nom_') && (
        <NomenclatureSection key={view} tab={view.slice(4)} />
      )}

      {view === 'recipes' && <RecipesPage />}
      {view === 'substitutes' && <SubstitutesPage />}
      {view === 'shortnames' && <ShortNames />}

      {/* Текстуры */}
      {view === 'textures' && (<>
        <TextureSection />
      </>)}

      {view === 'makers' && (<>
        <MaterialMakers />
      </>)}

      {/* Характеристики прессов */}
      {view === 'etalon' && (<>
        <EtalonModes />
      </>)}

      {view === 'pressSettings' && (<>
        <PressSettingsSection presses={presses} onUpdate={loadAll} />
      </>)}

      {/* Настройка сортов */}
      {view === 'grades' && (<>
        <GradesSection />
      </>)}

    </Layout>
  );
}
